'use client';

// РОДНОЕ ОКНО «СООБЩЕНИЕ» — ВОДИТЕЛЬ И МОЗГ СО СТОРОНЫ ВЕБА
// (ТЗ docs/MESSAGE-COMPOSER-PLAN.md, И9.6–И9.8, этапы Г14/Г15; образец — bcast-native.ts).
//
// Окно рисует натив (MessageScreen.swift) КАРТОЧКОЙ-ПРОТОТИПОМ письма, а веб остаётся
// мозгом целиком: он держит черновик, спрашивает у модели, что с чем уживается в ОДНОМ
// сообщении (draftShape/draftAttachOptions/draftIsOneMessage), считает разметку
// (message-marks.ts), заливает и готовит файлы тем же конвейером, что веб-окно, и
// присылает нативу ГОТОВЫЕ слоты и строки. Второй логики письма не существует.
//
// INVARIANT(scriptera): в Swift не бывает ни переводимых строк, ни правил «одного
// сообщения», ни пределов канала — всё, что уезжает нативу, посчитано здесь. Причина:
// правила Телеграма живут в одном словаре (channels.ts) и одной модели (tg-message.ts),
// язык кабинета меняется живьём, а сборка в сторе висит месяцами.
// Канон: CLAUDE.md запрет №8, docs/MESSAGE-COMPOSER-PLAN.md Д1.
//
// КАПКАН (повторы 29.08 и 31.08, bcast-native/wizard-native/account-native): у
// НАСТОЯЩЕГО моста addListener возвращает хендл БЕЗ .then — подписка ТОЛЬКО через await.
import { useEffect, useMemo, useRef, useState } from 'react';
import { letterBundleOf } from '@/lib/tg-letter-bundle';
import { useLocale, useTranslations } from 'next-intl';
import { TELEGRAM_MESSAGE_RULES as RULES, tgMediaMimeAccepted } from '@scriptera/ui/scenario';
import {
  bundleErrors,
  draftPreviewLink,
  draftShape,
  emptyDraft,
  sanitizeTgReply,
  tgAttachMediaKind,
  tgButtonStyleOf,
  tgButtonTakesReply,
  tgCanJoinPrev,
  normalizeButtonUrl,
  tgFlatButtons,
  tgRowsFromFlat,
  type TgAttachKind,
  type TgMediaKind,
  type TgDraft,
  type TgButton,
  type TgButtonStyle,
  type TgDraftMedia,
  draftMediaKeepable,
} from '@scriptera/db/tg-message';
// Служебные пометки ботов метками не являются (БЭКЛОГ п.13) — детектор ОДИН на продукт.
import { isServiceMark } from '@scriptera/db/bot-chains';
import { log as journalLog } from '@/lib/client-journal';
import {
  isNativeApp,
  nativeBridge,
  nativeMethod,
  readBubbleColors,
  readRoleHex,
  type NativeMsgData,
} from '@/lib/native-app';
import { attachFile, attachNeedsAsk, autoOp, fileFromDataUrl } from './message-composer/attach-file';
import { useMediaConvert, type ConvertOp } from './message-composer/use-media-convert';
import { useMsgConflictWords, useMsgDict } from './message-composer/use-msg-dict';
import {
  applyConflict,
  buttonAddConflict,
  conflictAsk,
  conflictKeep,
  doneConflict,
  mediaAddConflict,
  type MsgConflict,
  type MsgConflictAnswer,
} from './message-conflict';
import { applyFormat, markdownHtml, marksToTgHtml, shiftMarks, tgHtmlToMarks, type TgFormatKind } from './message-marks';
import {
  attachPickReplaces,
  msgAttachSheet,
  msgButtonPage,
  msgCanDone,
  msgCard,
  attachKindOf,
  attachAskHero,
  msgDefaultReply,
  msgRenameMedia,
  msgTextEdit,
  type MsgButtonDict,
  type MsgPinned,
} from './message-rows';
import { useLinkPreview, useMsgClock } from './message-composer/use-msg-live';
import { usePersistentData } from '@/lib/use-persistent-data';
import { refreshTagCaches } from '@/lib/tag-caches';
// Форматы словом человека («mp3, m4a») — общий словарь кабинета, не свой список.
import { mimeWord, mimeWords } from '@/lib/mime-words';
// Правила площадки по сторонам фото — общий чистый хелпер ручек заливки: второй правды
// о сторонах в продукте нет (числа живут в своде правил каналов).
import { photoDimsOk } from '@/lib/media-limits';

/** Сборка умеет родное окно письма — веб-шторка ей не нужна. */
export function nativeMessageSupported(): boolean {
  return isNativeApp() && nativeMethod('msgOpen');
}

/**
 * СКОЛЬКО БАЙТ ФАЙЛА ВЕЗЁМ МОСТОМ — ТОЛЬКО ДЛЯ СТАРОЙ СБОРКИ (ТЗ 20.09.2026 V.4 п.5).
 *
 * Файл едет СТРОКОЙ base64 и живёт сразу в двух памятях (оболочки и страницы), вырастая
 * против файла на треть; пределы канала доходят до 50 МБ — такую строку возить нельзя,
 * поэтому тяжелее натив файл не берёт и говорит об этом вебу.
 *
 * Сборка, умеющая `msgFileGo`, файл через мост НЕ ВОЗИТ ВОВСЕ — она заливает его сама, и
 * это число ей ничего не ограничивает. Слать его всё равно обязаны: старая сборка из
 * магазина без него возьмёт файл любого веса и повесит письмо (запрет №8).
 */
const BRIDGE_BYTES = 8 * 1024 * 1024;

/**
 * МЕСТО КНОПКИ В ПЛОСКОМ СПИСКЕ ПО КЛЮЧУ ПУЗЫРЯ (`btn:<ряд>:<номер в ряду>`).
 *
 * Ключ выдал сам веб (`msgCard`), Swift возвращает его как есть — поэтому разбираем
 * его здесь, а не считаем «ряд = номер кнопки»: с ТЗ М2.5 в ряду бывает до четырёх
 * кнопок. Старая сборка шлёт ключ без номера в ряду — это первая кнопка ряда.
 * Не нашли — −1: чужой ключ письмо не трогает.
 */
function flatIndexOfKey(flat: readonly { row: number }[], key: string): number {
  const parts = key.split(':');
  const row = Number(parts[1] ?? -1);
  if (!Number.isFinite(row) || row < 0) return -1;
  const raw = Number(parts[2] ?? 0);
  const inRow = Number.isFinite(raw) && raw >= 0 ? raw : 0;
  let seen = 0;
  for (let i = 0; i < flat.length; i += 1) {
    if (flat[i]!.row !== row) continue;
    if (seen === inRow) return i;
    seen += 1;
  }
  return -1;
}

/** Та же кнопка, но сразу записью плоского списка; null — ключ не про наши кнопки. */
function flatAtKey<T extends { row: number }>(flat: readonly T[], key: string): T | null {
  const at = flatIndexOfKey(flat, key);
  return at < 0 ? null : flat[at]!;
}

// ── Ключи правок страницы кнопки (ТЗ 21.09.2026 «Кнопка по-родному», раздел 5 п.4) ────

/**
 * РАЗБОР КЛЮЧА ПРАВКИ КНОПКИ. Ключи выдаёт веб же (`msgButtonPage`), но приезжают они с
 * родного экрана через мост — и разбор обязан быть проверяемым юнитом, а не «на глаз».
 *
 * Правило одно: `btn.<поле>` либо `btn.<поле>:<значение>`, и ЗНАЧЕНИЕ — ВЕСЬ ОСТАТОК
 * ключа. Прежний разбор резал ключ по каждой точке и двоеточию (`key.split(/[.:]/)`) и
 * терял имя метки «скидка: 20%» на первом же двоеточии — метки пишет человек, и ни
 * точка, ни двоеточие в них не запрещены.
 *
 * ЛИСТ МЕТОК УМЕЕТ ВСЁ ТО ЖЕ, ЧТО У КАРТОЧКИ СОБЕСЕДНИКА (владелец 21.09.2026: «там можно
 * добавить метку, удалить метку и галочку снять — а то, что ты сделал, там половину от
 * этого нельзя»). Поэтому ключей у меток три, и два из них меняют не кнопку, а общий
 * список: `btn.tag.new:<имя>` — завести метку, `btn.tag.del:<имя>` — снять её у ВСЕХ
 * (ровно тем же, чем это делает карточка человека). Третий — `btn.tag:<имя>` — ставит и
 * СНИМАЕТ метку у кнопки: тап несёт намерение, а не состояние экрана, и второго ключа
 * на снятие нет намеренно (см. `patchButton`).
 *
 * Ключи листа меток карточки человека (`ct:tag:add:` / `ct:tag:del:` / `ct:tag:rm:`)
 * понимаем тоже: родной лист у кнопки снят с `ContactTagsSheet`, и сборка вправе
 * прислать его собственные ключи. Смахивание в листе кнопки — всегда «у всех»: своего
 * списка меток у кнопки нет, снимать метку с одного человека здесь не с кого.
 *
 * Чужой ключ — null: письмо он не трогает.
 */
export function msgBtnKey(key: string): { field: string; arg: string } | null {
  if (key.startsWith('ct:tag:add:')) return { field: 'tagNew', arg: key.slice('ct:tag:add:'.length) };
  if (key.startsWith('ct:tag:del:')) return { field: 'tagDel', arg: key.slice('ct:tag:del:'.length) };
  if (key.startsWith('ct:tag:rm:')) return { field: 'tagDel', arg: key.slice('ct:tag:rm:'.length) };
  if (!key.startsWith('btn.')) return null;
  const rest = key.slice('btn.'.length);
  if (rest.startsWith('tag.new:')) return { field: 'tagNew', arg: rest.slice('tag.new:'.length) };
  if (rest.startsWith('tag.del:')) return { field: 'tagDel', arg: rest.slice('tag.del:'.length) };
  const cut = rest.search(/[.:]/);
  if (cut < 0) return rest === '' ? null : { field: rest, arg: '' };
  return { field: rest.slice(0, cut), arg: rest.slice(cut + 1) };
}

/**
 * РАСПОЛОЖЕНИЕ КНОПКИ ИЗ ПРАВКИ. Лист «Расположение» (ТЗ §4.3) — это ДВЕ строки-галочки
 * («Своим рядом» / «В одном ряду с предыдущей»), и обе приезжают как «выбрано»: решает
 * не `value`, а хвост ключа. Хвоста нет — прежняя одиночная галочка старой сборки, и
 * тогда решает `value` (запрет №8: старый ключ работает как работал).
 */
export function msgBtnJoin(arg: string, value: string | boolean): boolean {
  if (arg === '') return value === true || value === 'true';
  return arg !== 'own' && arg !== 'off' && arg !== 'false';
}

/**
 * ЗНАЧЕНИЕ ПРАВКИ, ПРИЕХАВШЕЙ ТАПОМ, — ПО ЖИВОМУ ЧЕРНОВИКУ, А НЕ ПО НАРИСОВАННОМУ ЭКРАНУ.
 *
 * КАПКАН ЗАЛПА (правило владельца 21.09.2026 «галочка сохраняет, крестик не сохраняет»):
 * родной лист копит правки у себя и по галочке шлёт их ПАЧКОЙ, в один тик. Экран в этот
 * миг показывает состояние ДО залпа, поэтому «переключить» обязано считаться от той
 * кнопки, что уже сложилась предыдущими правками пачки, — иначе второй тумблер в пачке
 * отменил бы первый.
 *
 * Тап несёт только ключ: у ВЫБОРА (вид, цвет, метка, «откуда отписать») значение — это
 * сам ключ, и ответ всегда «да»; у тумблера «и завершить эту цепочку» и у одиночной
 * галочки ряда старой сборки — переворот того, что стоит сейчас.
 */
export function msgBtnTapValue(field: string, arg: string, one: TgButton, joinPrev: boolean): boolean {
  if (field === 'joinPrev') return arg === '' ? !joinPrev : msgBtnJoin(arg, true);
  if (field === 'exit') return !(one.kind === 'tag' && one.exit === 'chain');
  return true;
}

/**
 * МЕТКА КНОПКИ ПОСЛЕ ПРАВКИ. Один ключ (`btn.tag:<имя>`) и ставит метку, и СНИМАЕТ её
 * (владелец 21.09.2026: «там можно добавить метку, удалить метку и галочку снять»):
 * повторный тап по отмеченной строке листа приезжает тем же ключом, и это значит «снять».
 *
 * INVARIANT(scriptera): второго ключа на снятие метки НЕ заводить. Причина: тап несёт
 * НАМЕРЕНИЕ, а не состояние экрана, и родной лист у кнопки — тот же `ContactTagsSheet`,
 * что на карточке собеседника; новый ключ развалил бы связку веба и сборки в сторе.
 * Канон: CLAUDE.md запрет №8.
 *
 * Имя пришло не хвостом ключа, а значением (человек набрал его в окошке «Новая метка»
 * или веб поставил метку сам) — это всегда постановка, и пустое значение стирает метку.
 */
export function msgBtnTag(now: string, arg: string, text: string): string {
  if (arg === '') return text;
  return now === arg ? '' : arg;
}

/** Подпись и цвет — общие у всех видов кнопки: вид их не касается (ТЗ М2.6, §4.5). */
function btnRepaint(one: TgButton, text: string, style?: TgButtonStyle): TgButton {
  const { style: _drop, ...rest } = one;
  const body = { ...rest, text } as TgButton;
  return style ? ({ ...body, style } as TgButton) : body;
}

/** Память настроек ПРЕЖНИХ видов одной кнопки: вид → какой кнопка при нём была. */
export type MsgBtnMemo = Partial<Record<TgButton['kind'], TgButton>>;

/**
 * СМЕНА ВИДА КНОПКИ НЕ СТИРАЕТ ЧУЖИЕ НАСТРОЙКИ МОЛЧА (ТЗ 21.09.2026 §4.5): адрес, метка
 * и ответ лежат в черновике каждый в своём поле, и вернувшийся вид приходит со своими
 * настройками. Человек в листе «Что делает» щёлкает виды, чтобы прочитать, что каждый
 * делает, — терять на этом набранный адрес было бы наказанием за любопытство.
 *
 * В ПИСЬМО уезжает только выбранный вид: память живёт в черновике страницы и умирает
 * вместе с ним («‹» выбрасывает черновик целиком).
 *
 * Вид встречается впервые — собираем его как раньше: у отписки подпись «Отписаться»
 * (своей нет) и ответ по умолчанию, у метки — только ответ.
 */
export function msgBtnKindSwitch(
  one: TgButton,
  kind: TgButton['kind'],
  memo: MsgBtnMemo,
  words: { replyTag: string; replyExit: string; exitText: string },
): { button: TgButton; memo: MsgBtnMemo } {
  const kept: MsgBtnMemo = { ...memo, [one.kind]: one };
  if (kind === one.kind) return { button: one, memo: kept };
  const was = kept[kind];
  if (was) return { button: btnRepaint(was, one.text, one.style), memo: kept };
  const paint = one.style ? { style: one.style } : {};
  const button: TgButton =
    kind === 'tag'
      ? { text: one.text, kind: 'tag', tag: '', reply: words.replyTag, ...paint }
      : kind === 'exit'
        ? {
            text: one.text.trim() === '' ? words.exitText : one.text,
            kind: 'exit',
            scope: 'chain',
            reply: words.replyExit,
            ...paint,
          }
        : kind === 'app'
          ? { text: one.text, kind: 'app', url: '', ...paint }
          : { text: one.text, kind: 'url', url: '', ...paint };
  return { button, memo: kept };
}

/** Вид кнопки из НЕДОВЕРЕННОГО хвоста ключа; чужое слово — не вид. */
export function msgBtnKindOf(raw: string): TgButton['kind'] | null {
  return raw === 'url' || raw === 'tag' || raw === 'exit' || raw === 'app' ? raw : null;
}

export interface NativeMessageOpts {
  active: boolean;
  data: NativeMsgData;
  /** Правка: 'text' (плоский текст и выделение), 'sel' (выделение), поле страницы кнопки. */
  onSet: (key: string, value: string | boolean, from: number, to: number) => void;
  /** Нажали кнопку под пузырём, «Добавить кнопку» или капсулу предела. `value` несёт то,
   *  что к ключу прилагается (порядок кнопок после перестановки), а `from`/`to` — отрезок
   *  из ползунка обрезки. */
  onTap: (key: string, value: string, from: number, to: number) => void;
  /** Тап по слоту пузыря: 'media' | 'text' | 'more'. */
  onSlot: (slot: string) => void;
  /** Кнопка полосы разметки на выделении. */
  /** `language` — язык блока кода (П150, аддитивно; старые сборки его не шлют). */
  onFormat: (kind: string, from: number, to: number, url: string, language: string) => void;
  /** Выбрали вид в листе «Вложение» (следом придёт файл тем же видом). */
  /** Выбрали вид в листе «Вложение». `replace` — тап по строке «Сейчас в сообщении»
   *  («Изменить»): файл ЗАМЕНЯЕТ стоящее, а не докладывается к нему. */
  onAttachPick: (kind: string, replace?: boolean) => void;
  /** «Убрать вложение» / «Убрать всё» / «Убрать это фото» (ключ файла; пусто — всё). */
  onAttachDrop: (key: string) => void;
  /** ПОРЯДОК ПАЛЬЦЕМ (П129.3): ключи файлов через пробел в новом порядке. */
  onAttachOrder: (keys: string) => void;
  /** Тумблер листа («spoiler», «captionAbove»; «previewOff» — от старых сборок). */
  onOption: (key: string, on: boolean) => void;
  /** Файл из родного выбора: `data:<mime>;base64,…`. ПУТЬ СТАРОЙ СБОРКИ (запрет №8):
   *  сборка с нативной заливкой шлёт `onFileState`, а сюда не приходит вовсе. */
  onFile: (kind: string, name: string, mime: string, data: string, replaceKey?: string) => void;
  /** ХОД ФАЙЛА, КОТОРЫЙ ГРУЗИТ САМА ОБОЛОЧКА (ТЗ 20.09.2026 V.4 п.5): 'picked' — файл
   *  выбран и лежит на телефоне (решает веб), 'compressing' — жмётся на устройстве,
   *  'uploading' — поехал, 'done' — залит (в `body` ответ нашей ручки), 'error'. */
  onFileState: (d: {
    kind: string;
    token: string;
    name: string;
    mime: string;
    bytes: number;
    seconds?: number;
    /** Стороны снимка с диска (ТЗ 20.09.2026 V.2): по ним решаются правила площадки. */
    width?: number;
    height?: number;
    /** Локальный адрес копии на телефоне (П129.2): по нему вложение рисуется сразу. */
    local?: string;
    /** Доля заливки 0…1 у `uploading` (П129.2) — кольцо на кадре. */
    progress?: number;
    /** Кадр круга с телефона (П129.4): локальный адрес jpg — пузырь рисует его до заливки. */
    poster?: string;
    /** ОПИСАНИЕ ОТ ТЕЛЕФОНА (П136 п.4б): есть ли звук; теги трека (ключ есть, пусть пустой, —
     *  телефон их читал). Уезжает с заливкой — ручка раннер не зовёт. */
    hasAudio?: boolean;
    title?: string;
    performer?: string;
    order?: number;
    status: string;
    body?: Record<string, unknown>;
    code?: string;
    /** ПЕРЕЭКСПОРТ ПОСЛЕ «РЕДАКТИРОВАТЬ» (П132 п.22): файл встаёт на место вложения с этим
     *  ключом, а не докладывается. */
    replaceKey?: string;
    /** КАДР И ОТРЕЗОК ИЗ РЕДАКТОРА КРУЖКА/ГИФКИ (П171): доли исходника 0…1 и секунды —
     *  хранятся в черновике и возвращаются редактору при «Редактировать». */
    crop?: { x: number; y: number; w: number; h: number };
    from?: number;
    to?: number;
    /** Личность снимка в галерее телефона (П173) — для галочек шторки скрепки. */
    assetId?: string;
  }) => void;
  /** Ответ родного короткого вопроса про файл (`wizAsk`); пустой ключ — «Отмена». */
  onAskPick: (key: string) => void;
  /** «Готово» в шапке. */
  onSubmit: () => void;
  /** Ушли назад («Назад», свайп). */
  onCancel: () => void;
}

/** Вопрос про файл, который ждёт своей очереди на родной лист (П129.2). */
interface MsgFileAsk {
  token: string;
  kind: TgMediaKind;
  /** Строка листа «Вложение», под которую открывали окно: ею же открываем его заново. */
  row: string;
  maxBytes: number;
  canSqueeze: boolean;
  go: (action: 'upload' | 'compress' | 'drop', maxBytes?: number) => void;
  text: string;
  actions: { key: string; label: string }[];
  /** «Герой» листа-вопроса (П135 п.11б): значок/кадр, имя, мета — от `attachAskHero`. */
  hero?: { icon: string; thumb?: string; name: string; meta: string };
  /** ЗАПИСЬ, КОТОРАЯ ВСТАНЕТ В СООБЩЕНИЕ ПО СОГЛАСИЮ (П132 п.19): файл не прошёл проверку
   *  ДО того, как встал в сообщение, и встанет только после «Сжать»/«Обработать». */
  place?: TgDraftMedia;
  /** На место какого вложения встанет (переэкспорт после «Редактировать», П132 п.22). */
  replaceKey?: string;
  /** ОТКАЗ, А НЕ ВОПРОС (П132 п.19): ручка или оболочка файл уже отбили — чинить нечего,
   *  остаётся «Выбрать другой файл» или «Отмена»; копии на телефоне для заливки нет. */
  rejected?: boolean;
  /** ЗАГОЛОВОК ВОПРОСА (П151 §11р п.3): «Кружок», «Несколько фотографий», «Файл слишком
   *  большой»; нет — общий заголовок листа «Вложение». Вопрос о составе (ТЗ П163) вдобавок
   *  говорит, кто забирает ответ (`onPick`: ключ действия, пустой — «Отмена»). Тот же
   *  родной лист `wizAsk` и та же очередь. */
  title?: string;
  onPick?: (key: string) => void;
  /** Своё слово отмены («Не добавлять кружок», владелец 27.09.2026 08:49); нет — «Отмена». */
  cancel?: string;
}

/** Ход файла от оболочки — аргумент `onFileState`. */
type MsgFileStateArg = Parameters<NativeMessageOpts['onFileState']>[0];

/** Ход файла письма в приложении (ТЗ 20.09.2026 V.4 п.5). */
/** 'meta' — файл ещё копируется из галереи, есть только место в отметке и кадр (П130 п.14). */
export type MsgFileStatus = 'meta' | 'picked' | 'compressing' | 'uploading' | 'done' | 'error';

/** Разобранное событие `msgFile`: либо ход нативной заливки, либо файл старой сборки. */
export interface MsgFileEvent {
  /** НОВАЯ ДОРОГА: файл грузит сама оболочка, сюда едет ход дела. */
  state?: {
    kind: string;
    token: string;
    name: string;
    mime: string;
    bytes: number;
    seconds?: number;
    width?: number;
    height?: number;
    local?: string;
    progress?: number;
    poster?: string;
    /** Описание от телефона (П136 п.4б): звук и теги трека — уезжают с заливкой. */
    hasAudio?: boolean;
    title?: string;
    performer?: string;
    /** Место в отметке галереи (П130 п.14). */
    order?: number;
    status: MsgFileStatus;
    body?: Record<string, unknown>;
    code?: string;
    /** Переэкспорт кружка/гифки после «Редактировать» (П132 п.22): на место этого ключа. */
    replaceKey?: string;
    /** Кадр и отрезок из редактора (П171), личность снимка в галерее (П173). */
    crop?: { x: number; y: number; w: number; h: number };
    from?: number;
    to?: number;
    assetId?: string;
  };
  /** ПУТЬ СТАРОЙ СБОРКИ (запрет №8): файл приехал строкой base64 через мост. */
  legacy?: { kind: string; name: string; mime: string; data: string; replaceKey?: string };
}

/**
 * ОПИСАНИЕ ФАЙЛА ОТ ТЕЛЕФОНА В АДРЕС ЗАЛИВКИ (П136 п.4б, доктрина HOW-IT-WORKS §11.4):
 * `&made=phone&seconds=…&width=…&height=…&hasAudio=0|1` (+ `title`/`performer`, если
 * телефон читал теги). Шлём только то, что оболочка сказала: нет ключа — нет параметра,
 * и ручка тогда узнаёт недостающее сама, как раньше. `made=phone` — всегда: файл
 * сделало приложение, а не браузер.
 */
export function phoneQuery(d: {
  seconds?: number;
  width?: number;
  height?: number;
  hasAudio?: boolean;
  title?: string;
  performer?: string;
}): string {
  const parts: [string, string][] = [['made', 'phone']];
  if (d.seconds !== undefined && d.seconds > 0) parts.push(['seconds', String(d.seconds)]);
  if (d.width !== undefined && d.width > 0) parts.push(['width', String(d.width)]);
  if (d.height !== undefined && d.height > 0) parts.push(['height', String(d.height)]);
  if (d.hasAudio !== undefined) parts.push(['hasAudio', d.hasAudio ? '1' : '0']);
  if (d.title !== undefined) parts.push(['title', d.title]);
  if (d.performer !== undefined) parts.push(['performer', d.performer]);
  return parts.map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join('');
}

/**
 * РАЗБОР СОБЫТИЯ `msgFile` — ОТДЕЛЬНО ОТ ПОДПИСКИ (хвост ТЗ блока V): решение «что это
 * приехало» проверяется юнитом, а не живым приложением.
 *
 * Две дороги в одном событии: сборка с нативной заливкой шлёт `status` (и `body` с
 * ответом нашей ручки), сборка из магазина — `data` строкой base64. Признак — именно
 * `status`: по нему и различаем, ничего не угадывая по другим ключам (запрет №8).
 *
 * Незнакомое состояние считаем «поехал»: молчать в ответ нельзя — ряд файла остался бы
 * без ответа, а письмо навсегда «занятым». Вес и секунды берём только настоящие: 0 и
 * пустота честнее выдуманного числа.
 */
/** Кадр из редактора (П171): четыре конечных числа в долях 0…1 с ненулевой стороной. */
function cropOf(raw: unknown): { x: number; y: number; w: number; h: number } | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
  if (!num(c.x) || !num(c.y) || !num(c.w) || !num(c.h) || c.w === 0 || c.h === 0) return null;
  return { x: c.x, y: c.y, w: c.w, h: c.h };
}

export function msgFileEvent(d: {
  kind?: string;
  token?: string;
  name?: string;
  mime?: string;
  bytes?: number;
  seconds?: number;
  width?: number;
  height?: number;
  local?: string;
  progress?: number;
  poster?: string;
  hasAudio?: boolean;
  title?: string;
  performer?: string;
  order?: number;
  status?: string;
  body?: Record<string, unknown>;
  code?: string;
  data?: string;
  replaceKey?: string;
  crop?: unknown;
  from?: number;
  to?: number;
  assetId?: string;
}): MsgFileEvent | null {
  if (typeof d.kind !== 'string' || d.kind === '') return null;
  // КАДР ИЗ РЕДАКТОРА (П171): доли 0…1, четыре числа; иначе ключа нет — целый кадр.
  const crop = cropOf(d.crop);
  // ЗАМЕНА НА МЕСТЕ (П132 п.22): ключ АДДИТИВНЫЙ — нет его или он пуст, дорога прежняя.
  const replaceKey =
    typeof d.replaceKey === 'string' && d.replaceKey !== '' ? { replaceKey: d.replaceKey } : {};
  if (typeof d.status === 'string' && d.status !== '') {
    const status: MsgFileStatus =
      d.status === 'meta' ||
      d.status === 'picked' ||
      d.status === 'compressing' ||
      d.status === 'done' ||
      d.status === 'error'
        ? d.status
        : 'uploading';
    return {
      state: {
        kind: d.kind,
        token: typeof d.token === 'string' ? d.token : '',
        name: typeof d.name === 'string' ? d.name : '',
        mime: typeof d.mime === 'string' ? d.mime : '',
        bytes: typeof d.bytes === 'number' && d.bytes > 0 ? d.bytes : 0,
        ...(typeof d.seconds === 'number' && d.seconds > 0 ? { seconds: d.seconds } : {}),
        // Стороны снимка: по ним решаются правила площадки ДО заливки.
        ...(typeof d.width === 'number' && d.width > 0 ? { width: d.width } : {}),
        ...(typeof d.height === 'number' && d.height > 0 ? { height: d.height } : {}),
        ...(typeof d.local === 'string' && d.local !== '' ? { local: d.local } : {}),
        ...(typeof d.poster === 'string' && d.poster !== '' ? { poster: d.poster } : {}),
        // ОПИСАНИЕ ОТ ТЕЛЕФОНА (П136 п.4б): ключи АДДИТИВНЫЕ — старая сборка их не шлёт, и
        // ручка тогда пробует файл раннером, как раньше. Пустой тег — «тегов нет», не пропуск.
        ...(typeof d.hasAudio === 'boolean' ? { hasAudio: d.hasAudio } : {}),
        ...(typeof d.title === 'string' ? { title: d.title } : {}),
        ...(typeof d.performer === 'string' ? { performer: d.performer } : {}),
        // Место в отметке галереи (П130 п.14).
        ...(typeof d.order === 'number' && d.order >= 0 ? { order: d.order } : {}),
        ...(typeof d.progress === 'number' && Number.isFinite(d.progress)
          ? { progress: Math.max(0, Math.min(1, d.progress)) }
          : {}),
        status,
        ...(d.body && typeof d.body === 'object' ? { body: d.body } : {}),
        ...(typeof d.code === 'string' && d.code !== '' ? { code: d.code } : {}),
        ...replaceKey,
        // КАДР И ОТРЕЗОК РЕДАКТОРА (П171), ЛИЧНОСТЬ СНИМКА (П173): ключи аддитивные — старая
        // сборка их не шлёт, и черновик живёт без них, как жил.
        ...(crop ? { crop } : {}),
        ...(typeof d.from === 'number' && Number.isFinite(d.from) && d.from >= 0 ? { from: d.from } : {}),
        ...(typeof d.to === 'number' && Number.isFinite(d.to) && d.to > 0 ? { to: d.to } : {}),
        ...(typeof d.assetId === 'string' && d.assetId !== '' ? { assetId: d.assetId } : {}),
      },
    };
  }
  if (typeof d.data === 'string' && d.data !== '') {
    return {
      legacy: {
        kind: d.kind,
        name: typeof d.name === 'string' ? d.name : '',
        mime: typeof d.mime === 'string' ? d.mime : '',
        data: d.data,
        ...replaceKey,
      },
    };
  }
  // Ни хода дела, ни файла — событию нечего сказать (пустой выбор, отменённое окно).
  return null;
}

/** Открыть родное окно и держать его в согласии с состоянием веба. */
export function useNativeMessage(opts: NativeMessageOpts): void {
  const { active, data } = opts;
  // Руки — рефами: подписка живёт одна, а обработчик всегда свежий (иначе слушатель
  // держал бы первый рендер с пустым черновиком).
  const ref = useRef(opts);
  ref.current = opts;
  const openedRef = useRef(false);
  /** Что уже уехало нативу: словарь пересобирается каждый рендер — шлём только
   *  настоящую разницу, а не каждый чих ререндера. */
  const sentRef = useRef('');

  useEffect(() => {
    if (!active) return;
    const br = nativeBridge();
    if (!br) return;
    const json = JSON.stringify(data);
    if (openedRef.current) {
      if (sentRef.current === json) return;
      sentRef.current = json;
      void br.msgPatch?.(data);
      return;
    }
    openedRef.current = true;
    sentRef.current = json;
    void br.msgOpen?.(data).then((res) => {
      journalLog('msg.open', { ok: res && (res as { ok?: boolean }).ok === false ? 0 : 1 });
      // Экран не встал (оболочка ещё не собрана) — отматываем «открыто» назад, и
      // ближайший патч поднимет его заново. Без этого человек остался бы ни с чем:
      // веб-окно под родным экраном не рисуется.
      if (res && (res as { ok?: boolean }).ok === false) {
        openedRef.current = false;
        sentRef.current = '';
      }
    });
  }, [active, data]);

  // Окно ушло (письмо забрали, закрыли страницу) — прибрать экран.
  useEffect(() => {
    if (!active) return;
    return () => {
      if (!openedRef.current) return;
      openedRef.current = false;
      sentRef.current = '';
      void nativeBridge()?.msgClose?.();
    };
  }, [active]);

  // Намерения родного экрана. Подписка через await (капкан в шапке файла).
  useEffect(() => {
    if (!active) return;
    const br = nativeBridge();
    if (!br?.addListener) return;
    let dead = false;
    const subs: Array<{ remove: () => void | Promise<void> }> = [];
    void (async () => {
      const add = async (
        ev:
          | 'msgSet'
          | 'msgTap'
          | 'msgFormat'
          | 'msgFile'
          | 'msgSubmit'
          | 'msgCancel'
          | 'msgSlotTap'
          | 'msgTextOpen'
          | 'msgTextSet'
          | 'msgAttachPick'
          | 'msgAttachDrop'
          | 'msgAttachOrder'
          | 'msgOptionSet'
          // Ответ родного короткого вопроса (`wizAsk`) — им окно письма спрашивает про
          // файл, который не подходит каналу (ТЗ V.2: второго вида листа не заводим).
          | 'wizAskPick',
        cb: (d: {
          key?: string;
          value?: string | boolean | string[];
          kind?: string;
          /** msgAttachPick: нажали строку «Сейчас в сообщении» («Изменить») — файл
           *  ЗАМЕНИТ стоящее, а не доложится к нему (владелец 21.09.2026). */
          replace?: boolean;
          slot?: string;
          /** msgAttachOrder (П129.3): ключи файлов через пробел в новом порядке. */
          keys?: string;
          from?: number;
          to?: number;
          url?: string;
          /** msgFormat (П150): язык блока кода `pre`; старые сборки не шлют. */
          language?: string;
          name?: string;
          mime?: string;
          data?: string;
          /** Файл, который грузит САМА ОБОЛОЧКА (ТЗ 20.09.2026 V.4 п.5). */
          bytes?: number;
          seconds?: number;
          width?: number;
          height?: number;
          status?: string;
          token?: string;
          body?: Record<string, unknown>;
          code?: string;
          /** msgFile: переэкспорт после «Редактировать» — на место этого вложения (П132 п.22). */
          replaceKey?: string;
          /** msgFile: кадр из редактора (П171) и личность снимка в галерее (П173). */
          crop?: unknown;
          assetId?: string;
        }) => void,
      ): Promise<void> => {
        const sub = await br.addListener!(ev, cb);
        if (dead) void sub.remove();
        else subs.push(sub);
      };
      await add('msgSet', (d) => {
        if (!d.key) return;
        const v = d.value;
        if (typeof v === 'string' || typeof v === 'boolean') {
          ref.current.onSet(d.key, v, d.from ?? 0, d.to ?? 0);
        }
      });
      await add('msgTap', (d) => {
        if (!d.key) return;
        ref.current.onTap(d.key, typeof d.value === 'string' ? d.value : '', d.from ?? 0, d.to ?? 0);
      });
      await add('msgFormat', (d) => {
        if (d.kind) ref.current.onFormat(d.kind, d.from ?? 0, d.to ?? 0, d.url ?? '', d.language ?? '');
      });
      await add('msgFile', (d) => {
        const got = msgFileEvent(d);
        if (!got) return;
        // НОВАЯ ДОРОГА (ТЗ V.4 п.5): файл грузит сама оболочка, а сюда едет ход дела.
        // Старая сборка `status` не шлёт — тогда прежний путь, файлом через мост.
        if (got.state) ref.current.onFileState(got.state);
        else if (got.legacy) {
          ref.current.onFile(
            got.legacy.kind,
            got.legacy.name,
            got.legacy.mime,
            got.legacy.data,
            got.legacy.replaceKey,
          );
        }
      });
      await add('wizAskPick', (d) => {
        ref.current.onAskPick(typeof d.key === 'string' ? d.key : '');
      });
      await add('msgSubmit', () => ref.current.onSubmit());
      await add('msgCancel', () => ref.current.onCancel());
      // ── Новые события карточки (только новыми именами — запрет №8) ────────────────
      await add('msgSlotTap', (d) => {
        if (d.slot) ref.current.onSlot(d.slot);
      });
      // МЁРТВОЕ СОБЫТИЕ (запрет №8): страницы «Текст» больше нет (И11.7), но сборка в
      // сторе её ещё умеет и о своём открытии сообщает — слушаем, чтобы не потерять
      // правки старой установки, и просто помечаем в журнале.
      await add('msgTextOpen', () => ref.current.onSlot('text'));
      await add('msgTextSet', (d) => {
        if (typeof d.value === 'string') ref.current.onSet('text', d.value, d.from ?? 0, d.to ?? 0);
      });
      await add('msgAttachPick', (d) => {
        if (d.kind) ref.current.onAttachPick(d.kind, d.replace === true);
      });
      await add('msgAttachDrop', (d) => ref.current.onAttachDrop(d.key ?? ''));
      await add('msgAttachOrder', (d) => ref.current.onAttachOrder(String(d.keys ?? '')));
      await add('msgOptionSet', (d) => {
        if (d.key) ref.current.onOption(d.key, d.value === true);
      });
    })();
    return () => {
      dead = true;
      for (const s of subs) void s.remove();
    };
  }, [active]);
}

// ── Родное окно как кусок веба (скрытый мозг) ────────────────────────────────────────

/**
 * МОЗГ РОДНОГО ОКНА. Стоит скрытым в дереве (сам ничего не рисует), а на экране человек
 * видит родной экран приложения. Здесь живёт всё, что окно знает: черновик письма, его
 * слоты, разметка текста, заливка и подготовка файлов, слова кабинета.
 */
/** ВСТАВИТЬ ПО МЕСТУ В ОТМЕТКЕ (П130 п.14): запись с `order` встаёт перед первой записью
 *  с бо́льшим номером; без номера (файлы, старая сборка) — в конец, как было. */
function insertByOrder(list: readonly TgDraftMedia[], media: TgDraftMedia): TgDraftMedia[] {
  if (media.order === undefined) return [...list, media];
  const at = list.findIndex((m) => m.order !== undefined && m.order > (media.order ?? 0));
  if (at < 0) return [...list, media];
  return [...list.slice(0, at), media, ...list.slice(at)];
}

export function MessageNativeHost({
  value,
  title,
  subtitle,
  flow = false,
  stack = false,
  pinned = [],
  buttonKinds,
  chainButtons = false,
  clockAt,
  onPinned,
  onChange,
  onSave,
  onCancel,
}: {
  value?: TgDraft;
  /** Письмо ЦЕПОЧКИ (BACKLOG п.19 (2)): у кнопки-метки есть «и завершить эту цепочку». */
  chainButtons?: boolean;
  /** Кнопки, по которым ветвится сценарий (ТЗ З7): в письме их нет, правится подпись. */
  pinned?: readonly MsgPinned[];
  /** Какие виды свободных кнопок предлагать (по умолчанию все четыре). */
  buttonKinds?: readonly TgButton['kind'][];
  /** Правку подписи приколотой кнопки забирает потребитель — она его ответ. */
  onPinned?: (key: string, text: string) => void;
  /** Заголовок окна («Сообщение» либо своё слово потребителя). */
  title?: string;
  /** Подзаголовок шапки — откуда пришли («Письмо 2 · через 1 день», «Новая рассылка»);
   *  совпал с заголовком — не рисуется (И11.8). */
  subtitle?: string;
  /** Окно открыто СО СТРАНИЦЫ ПИСЬМА цепочки — натив кладёт его страницей в ту же
   *  шторку (К23.1 п.3). Ключ аддитивный: старая сборка его не читает (запрет №8). */
  flow?: boolean;
  /** ПОЛНОЙ ШТОРКОЙ СНИЗУ ПОВЕРХ карточки письма (П130 п.13). Ключ аддитивный. */
  stack?: boolean;
  /** Когда письмо УЙДЁТ (ISO): его час стоит в углу пузыря, иначе — время сейчас. */
  clockAt?: string;
  /**
   * ПИСЬМО МЕНЯЕТСЯ — ЗАБИРАЙ СРАЗУ (ТЗ М2.2, владелец 16.09.2026: «нет кнопки, при этом
   * какие-то изменения применяются — непредсказуемая работа»). Зовётся на КАЖДУЮ правку
   * черновика: потребитель из мастера кладёт его в ответы анкеты, и тогда уход «‹»,
   * свайпом или возвратом со страницы письма ничего не теряет. Нет руки — окно ведёт
   * себя как раньше (письмо забирает только «Готово»).
   */
  onChange?: (draft: TgDraft) => void;
  /** Человек нажал «Готово» — забирай собранное письмо. */
  onSave: (draft: TgDraft) => void;
  /** Ушли назад, ничего не забрав. */
  onCancel: () => void;
}): null {
  const t = useTranslations('cabinet.message');
  // ОБЩИЕ слова о файле (вес, формат, «Сжать»/«Обработать») — один словарь на кабинет
  // (ТЗ 20.09.2026 V.2): мастер и окно письма говорят одинаково.
  const ts = useTranslations('cabinet.scenario');
  // «Слушать» и «Пауза» уже сказаны в словаре переписки — вторых слов не заводим.
  const tm = useTranslations('cabinet.messages');
  const locale = useLocale();
  const dict = useMsgDict();
  const cw = useMsgConflictWords();
  const [draft, setDraft] = useState<TgDraft>(() => value ?? emptyDraft());
  /** Где сейчас каретка или выделение в родном поле — от него же и приходит. */
  const [sel, setSel] = useState<{ from: number; to: number }>({ from: 0, to: 0 });
  /** Идёт заливка файла (подготовку у раннера считает `conv`). */
  const [uploading, setUploading] = useState(0);
  /** Каким видом ляжет файл, который человек сейчас выбирает (лист «Вложение»). */
  const [pick, setPick] = useState<{ kind: TgAttachKind; replace: boolean } | null>(null);
  /**
   * Какую кнопку правит человек — её место в ПЛОСКОМ списке кнопок письма (ТЗ М2.5:
   * кнопок в ряду бывает до четырёх, и «ряд» больше не адрес кнопки); null — страница
   * кнопки закрыта.
   */
  const [openBtn, setOpenBtn] = useState<number | null>(null);
  /**
   * ЧЕРНОВИК ОТКРЫТОЙ КНОПКИ (владелец 19.09.2026: «галочка сверху; не нажата — изменения
   * не производятся»). В сборке с галочкой «готово» (признак `msgButtonDone`) правки со
   * страницы копятся здесь, письмо не меняется; «готово» (`btn-done`) кладёт черновик в
   * письмо, «назад» (`btn-back`) его выбрасывает. `fresh` — кнопка новая: до «готово» её в
   * письме нет вовсе. Старая сборка без признака — прежний ход: правки сразу в письмо.
   */
  const [pendBtn, setPendBtn] = useState<{
    button: TgButton;
    joinPrev: boolean;
    fresh: boolean;
    /** Настройки прежних видов этой кнопки (ТЗ 21.09.2026 §4.5) — см. msgBtnKindSwitch. */
    memo: MsgBtnMemo;
  } | null>(null);
  const holdsDraft = (): boolean => nativeMethod('msgButtonDone');
  /**
   * ЖИВЫЕ ЧЕРНОВИК И МЕСТО КНОПКИ (правило владельца 21.09.2026 «галочка сохраняет,
   * крестик не сохраняет»): лист копит правки у себя и шлёт их ПАЧКОЙ по галочке — вся
   * пачка приходит в ОДИН тик, до перерисовки. Решать «черновик или сразу письмо» и «где
   * эта кнопка» по нарисованному экрану в такой миг нельзя: страница уже открыта, а
   * состояние рендера про это ещё не знает. Значения правок при этом считаются внутри
   * setPendBtn/setDraft — там `p` и `d` всегда свежие.
   */
  const livePend = useRef(pendBtn);
  livePend.current = pendBtn;
  const liveOpen = useRef(openBtn);
  liveOpen.current = openBtn;
  /** Какую ПРИКОЛОТУЮ кнопку правит человек (её ключ); null — её страница закрыта. */
  const [openPin, setOpenPin] = useState<string | null>(null);
  /** Подписи приколотых кнопок: правятся здесь, живут в ответах анкеты (З7). */
  const [pins, setPins] = useState<MsgPinned[]>(() => pinned.map((one) => ({ ...one })));
  const livePins = useRef(pins);
  livePins.current = pins;
  /** ГОТОВАЯ строка помехи, которую человек починит сам; пусто — помех нет. */
  const [failed, setFailed] = useState('');

  // Свежий черновик — обработчикам готовности: они срабатывают позже, чем ставились.
  const live = useRef(draft);
  useEffect(() => {
    live.current = draft;
  });

  /**
   * ПИСЬМО СОХРАНЯЕТСЯ ПРИ ЛЮБОМ УХОДЕ (ТЗ М2.2): черновик уезжает потребителю на
   * КАЖДУЮ правку, а не только по «Готово». Тогда «‹», свайп, возврат со страницы
   * письма (`wizItemRowsBack`) и галочка дают ОДИН результат — набранное остаётся.
   *
   * Сравниваем с тем, что уже отдали: словарь пересобирается каждый рендер, а слать
   * ответ анкеты на каждый чих ререндера — значит переписывать её вхолостую. Первый
   * рендер молчит: письмо и так пришло из ответа, отдавать его обратно нечего.
   */
  const sentRef = useRef<string | null>(null);
  useEffect(() => {
    const json = JSON.stringify(draft);
    if (sentRef.current === null) {
      sentRef.current = json;
      return;
    }
    if (sentRef.current === json) return;
    sentRef.current = json;
    // В ОТВЕТЫ АНКЕТЫ — БЕЗ НЕЗАЛИТЫХ ФАЙЛОВ (П129.2): запись с меткой оболочки вместо id
    // хранилища отправитель не нашёл бы; доедет — уедет следующим изменением.
    onChange?.({ ...draft, media: draftMediaKeepable(draft.media) });
  }, [draft, onChange]);

  /** Чем окно закрылось — для журнала (правило «наблюдаемость сначала», М2.2). */
  const leftBy = useRef('');
  useEffect(
    () => () => {
      // Ушли не галочкой и не «Назад» — значит экран сняли снаружи (возврат со страницы
      // письма, `wizItemRowsBack`). Письмо при этом УЖЕ в ответах анкеты (onChange выше).
      journalLog('msg.leave', { by: leftBy.current === '' ? 'rowsBack' : leftBy.current });
    },
    [],
  );
  const livePick = useRef(pick);
  livePick.current = pick;
  /** Очередь файлов одного захода: следующий грузится после предыдущего (см. onFile). */
  const fileQueue = useRef<Promise<void>>(Promise.resolve());
  /** Заданный родным листом вопрос про файл (ТЗ V.4 п.5): ответ приходит один. */
  /** ВОПРОСЫ ПРО ФАЙЛЫ — ПО ОЧЕРЕДИ (П129.2): файлы приезжают пачкой, родной лист один. */
  const askQueue = useRef<MsgFileAsk[]>([]);
  /** ОДИН ВОПРОС НА РЯД (П133 п.8в, владелец 25.09.2026: «две плашки подряд про вес»):
   *  по тому же файлу (`token`) или, без метки, по той же строке вопрос уже открыт или
   *  стоит в очереди — второй не задаём: прежний заход отброшен, ответ придёт один. */
  const askDup = (token: string, row: string): boolean => {
    const same = (a: MsgFileAsk): boolean => (token !== '' ? a.token === token : a.token === '' && a.row === row);
    return (askFile.current ? same(askFile.current) : false) || askQueue.current.some(same);
  };
  /** Файлы, что сейчас едут или жмутся (по метке): счётчик `uploading` растёт по файлу,
   *  а не по каждому событию хода. */
  const inFlight = useRef(new Set<string>());
  const askFile = useRef<MsgFileAsk | null>(null);
  /** ЗАМЕНА НА МЕСТЕ (П132 п.22): черновая запись переэкспорта → вложение, которое она
   *  временно заняла. Файл не доехал (отказ, «Отмена», копии нет) — прежнее возвращается
   *  на своё место; доехал — запись забывается. */
  const replacedRef = useRef(new Map<string, TgDraftMedia>());
  /** Место в отметке и кадр файла, чью плитку не поставили по `meta` (формат мимо канала,
   *  П132 п.19): понадобятся, если человек согласится «Обработать». */
  const metaHeld = useRef(new Map<string, { poster?: string; order?: number; assetId?: string }>());
  /**
   * ЗАХОД ВЫБОРА ФАЙЛОВ (ТЗ П163): что стояло до него (`before`), заменяет ли он стоящее и
   * какие файлы уже пропущены в сообщение (`ok`, по метке — с видом). Файлы приезжают
   * ПАЧКОЙ быстрее перерисовки, поэтому состав считаем отсюда, а не по нарисованному.
   * `allow` — человек ответил «Убрать …», дальше пускаем всё; `deny` — «Оставить одно»
   * или «Отмена», остальные файлы захода не кладём. Сбрасывается строкой листа.
   */
  const pickRun = useRef<{
    before: TgDraftMedia[];
    replace: boolean;
    ok: Map<string, TgMediaKind>;
    allow: boolean;
    deny: boolean;
  } | null>(null);
  /** Открытый вопрос о составе: события файлов, что ждут ответа (по порядку прихода). */
  const conflictHeld = useRef<MsgFileStateArg[] | null>(null);
  /** Файлы, которые по ответу в сообщение не идут: их события пропускаем, копию — `drop`. */
  const deniedFiles = useRef(new Set<string>());

  const conv = useMediaConvert({
    onDone: (key, op, out) => {
      if (op === 'waveform') {
        if (out.peaks && out.peaks.length > 0) {
          const peaks = out.peaks;
          setDraft((d) => ({ ...d, media: d.media.map((m) => (m.id === key ? { ...m, peaks } : m)) }));
        }
        return;
      }
      if (!out.id) return;
      const id = out.id;
      setDraft((d) => ({
        ...d,
        media: d.media.map((m) =>
          m.id === key
            ? {
                ...m,
                id,
                mime: out.mime ?? m.mime,
                bytes: out.bytes ?? m.bytes,
                ...(out.seconds !== undefined ? { seconds: out.seconds } : {}),
                // Кадр-постер готового ролика (П129.5); нет нового — прежний годится.
                ...(out.poster ?? m.poster ? { poster: out.poster ?? m.poster } : {}),
                derivedFrom: m.derivedFrom ?? key,
              }
            : m,
        ),
      }));
      // У готового голосового просим ещё и волну — слот рисует настоящую, а не узор.
      if (op === 'voice') conv.start(id, id, 'waveform');
    },
    onFail: (_key, op) => {
      journalLog('msg.convFailShown', { op });
      setFailed(t('errConvert'));
    },
  });

  // МЕТКИ КНОПКИ — ПОМЕТКИ БОТОВ (владелец 10.09.2026, БЭКЛОГ п.13): кнопка «Поставить
  // метку» вешает ФАКТ, и подсказывать в ней ручные метки клиента было бы обманом. Свод
  // общий (тот же адрес и ключ кэша, что у «Данных людей»), служебные он уже отрезал —
  // второго источника меток в продукте нет.
  const { data: tagSummary, reload: reloadTags } = usePersistentData<{
    ok?: boolean;
    tags?: Array<{ name: string }>;
  }>('cabinet:contact-tags', '/api/cabinet/contact-tags', {
    accept: (raw) => !!(raw as { ok?: boolean } | null)?.ok,
  });
  /**
   * МЕТКИ, ЗАВЕДЁННЫЕ ПРЯМО В ЛИСТЕ КНОПКИ (ТЗ 21.09.2026 §4.5.2, ключ `btn.tag.new:`).
   *
   * Живут только в окне. Справочника меток в продукте НЕТ: свод кабинета считается по
   * карточкам людей (`unnest(tags)`, contact-tags-cache.ts), и метка, которую ещё никому
   * не поставили, в нём появиться не может. Поставит её сама кнопка, когда человек её
   * нажмёт, — до тех пор новое имя видно в списке листа, и этого достаточно.
   */
  const [newTags, setNewTags] = useState<readonly string[]>([]);
  const tagOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const one of tagSummary?.tags ?? []) if (one.name.trim() !== '') seen.add(one.name);
    for (const one of newTags) if (one.trim() !== '') seen.add(one);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [tagSummary, newTags]);

  // ЧАСЫ И КАРТОЧКА ПРЕВЬЮ — общий мозг окна (И11.7): те же хуки кормят браузер.
  const clock = useMsgClock(clockAt);
  const preview = useLinkPreview(draftPreviewLink(draft));
  const marked = useMemo(() => tgHtmlToMarks(draft.text), [draft.text]);
  const bundle = useMemo(() => letterBundleOf(draft), [draft]);
  // Что ещё готовит раннер: слот такого файла стоит с кольцом хода и подписью.
  const busyIds = draft.media.filter((m) => conv.busy(m.id)).map((m) => m.id);
  const issues = useMemo(() => bundleErrors(bundle, RULES), [bundle]);

  // Наши поломки клиенту не показываем ВООБЩЕ (запрет №7) — они уходят в журнал,
  // который читаем мы. Человеку остаётся только то, что он сам починит.
  const ourKeys = issues
    .filter((i) => i.blame === 'us')
    .map((i) => i.key)
    .join(',');
  useEffect(() => {
    if (ourKeys !== '') journalLog('msg.issue', { keys: ourKeys });
  }, [ourKeys]);

  const btnDict: MsgButtonDict = useMemo(
    () => ({
      title: t('btnPageTitle'),
      text: t('btnText'),
      textPh: t('btnTextPh'),
      kind: t('btnKind'),
      kindName: (kind) => t(`btnKind_${kind}`),
      kindHint: (kind) => t(`btnKindHint_${kind}`),
      url: t('btnUrl'),
      urlHint: t('btnUrlHint'),
      appUrl: t('btnAppUrl'),
      appUrlHint: t('btnAppUrlHint'),
      tag: t('btnTag'),
      tagPh: t('btnTagPh'),
      tagHint: t('btnTagHint'),
      tagKnown: t('btnTagKnown'),
      tagExit: t('btnTagExit'),
      tagExitHint: t('btnTagExitHint'),
      // РАСПОЛОЖЕНИЕ и ЦВЕТ (ТЗ М2.5/М2.6).
      layout: t('btnLayout'),
      joinPrev: t('btnJoinPrev'),
      joinPrevHint: t('btnJoinPrevHint'),
      joinFull: t('btnJoinFull'),
      style: t('btnStyle'),
      styleHint: t('btnStyleHint'),
      styleName: (style) => t(`btnStyle_${style}`),
      scope: t('btnExitScope'),
      scopeChain: t('btnExit_chain'),
      scopeAll: t('btnExit_all'),
      scopeChainHint: t('btnExitHint_chain'),
      scopeAllHint: t('btnExitHint_all'),
      reply: t('btnReply'),
      replyHint: t('btnReplyHint'),
      replyPh: t('btnReplyPh'),
      replyTag: t('btnReply_tag'),
      replyExit: t('btnReply_exit'),
      remove: t('btnRemove'),
      done: t('done'),
      // СЛОВА НОВОЙ ФОРМЫ СТРАНИЦЫ — четыре строки и их листы (ТЗ 21.09.2026). Пока этого
      // блока нет, `msgButtonRows` возвращает null и натив рисует прежнюю страницу: так
      // ветки и сводились по отдельности. Удаления метки в листе НЕТ (владелец 21.09.2026),
      // поэтому слов о нём здесь тоже нет.
      rows: {
        noText: t('btnNoText'),
        // Заголовок первой карточки страницы (владелец 21.09.2026: «пузырь под названием
        // вид, где будет подпись, расположение и цвет»).
        view: t('btnGroupView'),
        ownRow: t('btnOwnRow'),
        notSet: t('btnNotSet'),
        tagAdd: t('btnTagAdd'),
        tagAddTitle: t('btnTagAddTitle'),
        tagAddPh: t('btnTagAddPh'),
        tagShared: t('btnTagShared'),
        // МЕТКА СТАВИТСЯ, СНИМАЕТСЯ И УДАЛЯЕТСЯ (владелец 21.09.2026): «Не выбрана» в
        // строке, слово смахивания и вопрос подтверждения — лист меток кнопки умеет то
        // же, что лист карточки собеседника.
        tagNone: t('btnTagNone'),
        tagDel: t('btnTagDel'),
        tagDelAsk: t('btnTagDelAsk'),
        urlBad: t('btnUrlBad'),
        // КАРТОЧКА «ПОСЛЕ НАЖАТИЯ» И СЛОВА ПОГАШЕННОЙ ГАЛОЧКИ (ТЗ П155 п.1.4, 1.9).
        afterTapTitle: t('btnAfterTapTitle'),
        needText: t('btnNeedText'),
        needUrl: t('btnNeedUrl'),
      },
    }),
    [t],
  );

  /** Кнопки письма ПЛОСКИМ списком: страница правит одну из них по её месту (М2.5). */
  const flatButtons = useMemo(() => tgFlatButtons(draft.buttons), [draft.buttons]);
  /** Кнопка, которую сейчас правят (её страница уехала нативу). */
  const openButton: TgButton | null =
    openBtn === null ? null : (pendBtn?.button ?? flatButtons[openBtn]?.button ?? null);
  /** Приколотая кнопка, которую сейчас правят: у неё есть только подпись (З7). */
  const openPinned: MsgPinned | null =
    openPin === null ? null : (pins.find((one) => one.key === openPin) ?? null);

  const busyNow = conv.anyBusy || uploading > 0;

  // Помеха, которую человек чинит САМ: слишком тяжёлый файл, слишком длинный кружок,
  // кнопка без адреса. Наши поломки сюда не попадают (они уже ушли в журнал выше).
  const mb = (n: number): number => Math.round(n / (1024 * 1024));
  const clientIssue = issues.find((i) => i.blame === 'client' && i.key !== 'empty');
  const issueText = !clientIssue
    ? ''
    : clientIssue.key === 'mediaTooBig'
      ? t('mediaTooBig', { has: mb(clientIssue.value ?? 0), max: mb(clientIssue.max ?? 0) })
      : clientIssue.key === 'roundTooLong'
        ? t('mediaTooLong', { max: clientIssue.max ?? 0 })
        : // Формат, который площадка не примет (перевод раннером не удался или файл
          // пришёл из старого письма): та же дорога, что у тяжёлого файла.
          clientIssue.key === 'mediaFormat'
          ? t('mediaFormat')
          : clientIssue.key === 'buttonNoUrl'
            ? t('errBtnNoUrl')
            : '';

  const dark = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark';
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  const data: NativeMsgData = useMemo(
    () => ({
      theme: dark ? 'dark' : 'light',
      title: title ?? t('title'),
      // Подзаголовок — откуда пришли; совпал с заголовком — не рисуем (И11.8).
      subtitle: subtitle && subtitle !== (title ?? t('title')) ? subtitle : '',
      // Страница шторки письма (К23.1 п.3) — только когда окно открыли с письма.
      ...(flow ? { flow: true } : {}),
      // ПОЛНОЙ ШТОРКОЙ СНИЗУ ПОВЕРХ КАРТОЧКИ ПИСЬМА (П130 п.13, владелец 24.09.2026).
      ...(stack ? { stack: true } : {}),
      dict: {
        done: t('done'),
        back: t('back'),
        cancel: t('cancel'),
        // С П150 это название подменю «Форматировать» (приказ координатора 25.09); сборка
        // с полосой показывает то же слово подписью полосы.
        format: t('fmt.format'),
        play: tm('voicePlay'),
        pause: tm('voicePause'),
        // Окошко адреса ссылки в приложении (сборка ≥125): заголовок, поле, «Вставить».
        // Старые сборки лишних слов не читают.
        linkTitle: t('fmtLink'),
        linkPh: t('linkPh'),
        linkOk: t('linkInsert'),
        // Подменю «Форматировать», алерт ссылки и тост кода (ТЗ П150 §2.4, §3.6–3.7) —
        // слова Telegram-iOS слово в слово; старые сборки ключ не читают.
        fmt: {
          format: t('fmt.format'),
          quote: t('fmt.quote'),
          spoiler: t('fmt.spoiler'),
          bold: t('fmt.bold'),
          italic: t('fmt.italic'),
          monospace: t('fmt.monospace'),
          link: t('fmt.link'),
          strikethrough: t('fmt.strikethrough'),
          underline: t('fmt.underline'),
          code: t('fmt.code'),
          linkAdd: t('fmt.linkAdd'),
          linkEdit: t('fmt.linkEdit'),
          linkText: t('fmt.linkText'),
          linkPh: t('fmt.linkPh'),
          cancel: t('fmt.cancel'),
          done: t('fmt.done'),
          codeCopied: t('fmt.codeCopied'),
        },
        // Строка ввода над клавиатурой (ТЗ П151 §8): плейсхолдер, галочка и скрепка для
        // читалки. Ключ новый — старая сборка его не читает.
        inputBar: dict.inputBar,
        // Доступ к фото своей сетки галереи (ТЗ П151 §11ж п.1): лист отказа и «Выбрать ещё».
        // Ключ новый — старая сборка его не читает.
        photoAccess: dict.photoAccess,
      },
      card: msgCard(draft, origin, locale, dict, { busyIds, pinned: pins, clock, preview }),
      text: msgTextEdit(draft, marked, sel, dict),
      attach: msgAttachSheet(draft, dict, pins, {
        origin,
        locale,
        preview,
        // ОБОЛОЧКА ГРУЗИТ ФАЙЛ САМА (ТЗ V.4 п.5) — только сборке, которая это умеет
        // (признак способности — метод `msgFileGo`); прочим прежняя дорога через мост.
        ...(nativeMethod('msgFileGo') ? { upload: { url: `${origin}/api/cabinet/media`, field: 'file' } } : {}),
      }),
      ...(openPinned
        ? {
            button: msgButtonPage({ text: openPinned.text, kind: 'url', url: '' }, 0, [], btnDict, {
              textOnly: true,
              key: `pin:${openPinned.key}`,
            }),
          }
        : openButton && openBtn !== null
          ? {
              button: msgButtonPage(openButton, openBtn, tagOptions, btnDict, {
                ...(buttonKinds ? { kinds: buttonKinds } : {}),
                ...(chainButtons ? { chain: true } : {}),
                // РАСПОЛОЖЕНИЕ (М2.5): у ПЕРВОЙ кнопки письма группы нет вовсе —
                // присоединяться ей не к чему.
                ...(openBtn > 0
                  ? {
                      layout: {
                        joinPrev: pendBtn ? pendBtn.joinPrev : flatButtons[openBtn]?.joinPrev === true,
                        // У НОВОЙ кнопки (черновик, в письме её ещё нет) — считаем по письму
                        // с ней в конце, иначе «присоединить к предыдущей» отвечало бы не о ней.
                        disabled: !tgCanJoinPrev(
                          pendBtn?.fresh
                            ? tgRowsFromFlat(
                                [...flatButtons.map((x) => ({ button: x.button, joinPrev: x.joinPrev })), { button: pendBtn.button, joinPrev: false }],
                                RULES.buttonsPerRowMax,
                              )
                            : draft.buttons,
                          openBtn,
                          RULES.buttonsPerRowMax,
                        ),
                      },
                    }
                  : {}),
              }),
            }
          : {}),
      bridgeBytes: BRIDGE_BYTES,
      // «ФАЙЛ ГОТОВИТСЯ…» ПОД ПУЗЫРЁМ НЕ ПИШЕМ (П135 п.7б, владелец 25.09.2026): ход файла
      // показывает кольцо на самом вложении; строка старым сборкам остаётся пустой.
      busy: '',
      error: failed || issueText,
      canDone: msgCanDone(draft, busyNow, pins),
      // ОКНО НЕ ЗАКРЫВАЕТСЯ, ПОКА ФАЙЛ ЕДЕТ (П132 п.2, КАПКАН: уход во время заливки терял
      // файл — незалитое в ответы анкеты не пишется, очередь умирает с окном). Сорвавшийся
      // файл окно не держит: он ждёт «Повторить», а уйти человек вправе.
      ...(draft.media.some((m) => m.pending === true && m.failed !== true) ? { lock: true } : {}),
      skin: {
        bg: readRoleHex('--bg', dark ? '#101014' : '#fbfbfd'),
        spotPink: readRoleHex('--accent-soft', '#ffe7ed'),
        spotGreen: readRoleHex('--green-soft', '#e0f5f7'),
        // Голубой Телеграма — ЕГО фирменный цвет, не наша палитра: им красятся
        // inline-кнопки, как в самом Телеграме (CLAUDE.md «Цвет по ролям»: чужие
        // фирменные цвета не перекрашиваем).
        blue: readRoleHex('--tg-blue', '#2aabee'),
        // Цвета пузырей — ОБЩАЯ рука с родной перепиской: второй палитры пузыря в
        // приложении не бывает.
        bubble: readBubbleColors(),
        // ПРОТОТИП ПОКАЗЫВАЕТ ТЕЛЕГРАМ, А НЕ НАШУ ПАЛИТРУ (И11.7): пузырь входящий,
        // белый (в темноте — тёмный пузырь Телеграма), на фоне его чата. Эти две роли
        // заведены только под прототип письма (И11.9 п.5) — в остальном кабинете их нет.
        tgBubbleIn: readRoleHex('--tg-bubble-in', dark ? '#212121' : '#ffffff'),
        tgChatBg: readRoleHex('--tg-chat-bg', dark ? '#0f0f0f' : '#fbfaf9'),
        // Серый «тихого языка»: им говорят ВСЕ места «добавить» (пунктир, слово, значок).
        mut: readRoleHex('--mut', dark ? '#9aa1ab' : '#8a9099'),
        // ОБВОДКА ПУЗЫРЯ (ТЗ М2.3/М3.2, владелец 16.09.2026: «пузырь белый, очертаний не
        // видно»): роль линии кабинета, та же, что рисует обводку в браузере.
        line: readRoleHex('--line', dark ? '#2b2f36' : '#e9ebef'),
        // ЦВЕТА КНОПОК ПО `style` — ОБЫЧНЫЕ, НЕ НАШИ (владелец 21.09.2026: «зелёный у нас
        // бирюзовый»). Кнопку красит сам Телеграм системными цветами iOS, поэтому здесь
        // роли прототипа --tg-green / --tg-red, а не палитра кабинета (--green — бирюза,
        // --step-error — наш сигнальный). Синий берётся из `blue` (голубой Телеграма).
        green: readRoleHex('--tg-green', dark ? '#30d158' : '#34c759'),
        danger: readRoleHex('--tg-red', dark ? '#ff453a' : '#ff3b30'),
      },
    }),
    [dark, title, subtitle, flow, stack, t, tm, draft, origin, locale, dict, btnDict, marked, busyIds, sel,
     busyNow, failed, issueText, openBtn, openButton, openPinned, pins, buttonKinds, chainButtons, tagOptions,
     clock, preview, flatButtons],
  );

  /** Приложить файл, приехавший из родного пикера СТРОКОЙ ЧЕРЕЗ МОСТ. ПУТЬ СТАРОЙ
   *  СБОРКИ (ТЗ 20.09.2026 V.4 п.5): новая оболочка заливает файл сама и присылает
   *  готовый ответ ручки (`onFileState`), сюда не заходя. */
  const takeFile = async (
    kind: string,
    name: string,
    mime: string,
    dataUrl: string,
    replaceKey?: string,
  ): Promise<void> => {
    const file = fileFromDataUrl(dataUrl, name, mime);
    if (!file) {
      journalLog('msg.fileBroken', { kind });
      setFailed(t('errUpload'));
      return;
    }
    setFailed('');
    setUploading((n) => n + 1);
    try {
      // Вид медиа решает mime, а не человек: «Фото или видео» — один пункт листа.
      const want = (kind as TgAttachKind) ?? 'document';
      const got = await attachFile(file, tgAttachMediaKind(want, mime));
      if (!got.media) {
        // ОТКАЗ ФАЙЛА — РОДНЫМ ВОПРОСОМ, А НЕ КРАСНОЙ СТРОКОЙ У ПУЗЫРЯ (П132 п.19). Сборка
        // без вопроса (`wizAsk`) — прежняя строка: сказать человеку больше нечем.
        if ((got.fail === 'too_large' || got.fail === 'bad_type') && nativeMethod('wizAsk')) {
          rejectFile({ token: '', kind: tgAttachMediaKind(want, mime), row: want, code: got.fail });
          return;
        }
        setFailed(
          got.fail === 'too_large'
            ? t('errTooLarge', { mb: mb(RULES.uploadAcceptMax) })
            : got.fail === 'bad_type'
              ? t('errBadType')
              : t('errUpload'),
        );
        return;
      }
      const media: TgDraftMedia = { ...got.media, kind: tgAttachMediaKind(want, got.media.mime) };
      // ПЕРЕЭКСПОРТ ПОСЛЕ «РЕДАКТИРОВАТЬ» (П132 п.22) — на место правленого вложения.
      if (replaceKey && live.current.media.some((m) => m.id === replaceKey)) {
        setDraft((d) => ({ ...d, media: d.media.map((m) => (m.id === replaceKey ? media : m)) }));
        if (got.op) conv.start(media.id, media.id, got.op);
        return;
      }
      putMedia(media, got.op ?? null);
    } finally {
      setUploading((n) => Math.max(0, n - 1));
    }
  };

  /**
   * ГОТОВЫЙ ФАЙЛ — В ПИСЬМО. Одна рука на обе дороги: файл, залитый вебом (старая
   * сборка, мост), и файл, залитый САМОЙ ОБОЛОЧКОЙ (ТЗ 20.09.2026 V.4 п.5).
   */
  const putMedia = (media: TgDraftMedia, op: ConvertOp | null): void => {
    // «Заменить» кладёт файл ВМЕСТО стоящего, «Добавить» — рядом (альбом).
    const replace = livePick.current?.replace === true;
    // ПЕРВЫЙ ФАЙЛ ЗАХОДА ЗАМЕНЯЕТ, ОСТАЛЬНЫЕ ДОКЛАДЫВАЮТСЯ. Родной выбор отдаёт альбом
    // ПАЧКОЙ (до десяти файлов подряд), и ждать перерисовки нельзя — признак гасим
    // прямо в живом значении, а не только в состоянии: иначе второй файл тоже счёл бы
    // себя заменой и стёр первый.
    if (replace) {
      livePick.current = livePick.current ? { ...livePick.current, replace: false } : null;
      setPick((prev) => (prev ? { ...prev, replace: false } : prev));
    }
    setDraft((d) => ({ ...d, media: replace ? [media] : insertByOrder(d.media, media) }));
    if (op) conv.start(media.id, media.id, op);
  };

  /** Поправить черновую запись по метке файла (ход, срыв). */
  const patchPending = (token: string, patch: Partial<TgDraftMedia>): void => {
    setDraft((d) => ({ ...d, media: d.media.map((m) => (m.id === token ? { ...m, ...patch } : m)) }));
  };
  /** Убрать черновую запись (отмена вопроса, «загрузить другой файл», копии нет). Запись
   *  заняла место правленого кружка/гифки (`replaceKey`) — прежний файл встаёт обратно. */
  const dropPending = (token: string): void => {
    // Файл, который оболочка уже начала жать, но человек отказался, — больше не «в пути»:
    // иначе счётчик заливки навсегда держал бы письмо занятым.
    if (inFlight.current.delete(token)) setUploading((n) => Math.max(0, n - 1));
    pickRun.current?.ok.delete(token);
    const was = replacedRef.current.get(token);
    replacedRef.current.delete(token);
    setDraft((d) => ({
      ...d,
      media: was ? d.media.map((m) => (m.id === token ? was : m)) : d.media.filter((m) => m.id !== token),
    }));
  };
  /**
   * ЧЕРНОВАЯ ЗАПИСЬ — В СООБЩЕНИЕ. Обычный файл ложится как всегда (`putMedia`: замена или
   * доклад по листу), а переэкспорт после «Редактировать» (П132 п.22) — НА МЕСТО вложения
   * `replaceKey`: тот же порядок, та же позиция. Вложения с таким ключом уже нет (убрали
   * крестиком) — ложится как обычный файл.
   */
  const placePending = (media: TgDraftMedia, replaceKey?: string): void => {
    const was = replaceKey ? live.current.media.find((m) => m.id === replaceKey) : undefined;
    if (!replaceKey || !was) {
      putMedia(media, null);
      return;
    }
    replacedRef.current.set(media.id, was);
    setDraft((d) => ({ ...d, media: d.media.map((m) => (m.id === replaceKey ? media : m)) }));
    journalLog('msg.fileReplaceAt', { kind: media.kind });
  };
  /** Файл доехал: черновая запись становится настоящей НА ТОМ ЖЕ МЕСТЕ (порядок альбома —
   *  как отметил человек), потом — что попросить у раннера. Запись уже убрали крестиком —
   *  ничего не кладём. */
  const settlePending = (token: string, media: TgDraftMedia, op: ConvertOp | null): void => {
    replacedRef.current.delete(token);
    let kept = false;
    setDraft((d) => {
      if (!d.media.some((m) => m.id === token)) return d;
      kept = true;
      // КОПИЯ НА ТЕЛЕФОНЕ ОСТАЁТСЯ У ЗАЛИТОГО ФАЙЛА (П133 п.4, владелец 25.09.2026: «я же
      // его загружал — качать не должно»): серверная запись наследует `local` от записи
      // `pending`, лист «Вложение» шлёт его в `view.local`, и просмотр открывает файл с
      // телефона, а не с сервера. Чужой/пропавший путь натив отвергает сам (проверка файла).
      return {
        ...d,
        // РАСКЛАДКА НЕ МЕНЯЕТСЯ ПО `done` (П134 п.4, владелец 25.09.2026: «по завершении
        // какое-то дёрганье»): чего сервер не вернул — превью, стороны, длительность, имя —
        // остаётся от записи `pending`; пузырь получает ту же картинку тех же размеров и
        // гасит только кольцо.
        media: d.media.map((m) =>
          m.id === token
            ? {
                ...media,
                ...(m.local ? { local: m.local } : {}),
                ...(media.poster === undefined && m.poster ? { poster: m.poster } : {}),
                ...(media.width === undefined && m.width !== undefined ? { width: m.width } : {}),
                ...(media.height === undefined && m.height !== undefined ? { height: m.height } : {}),
                ...(media.seconds === undefined && m.seconds !== undefined ? { seconds: m.seconds } : {}),
                ...(media.name === undefined && m.name ? { name: m.name } : {}),
                // Кадр и отрезок редактора (П171), личность снимка (П173) — сервер их не знает,
                // остаются от записи `pending`: «Редактировать» откроется с выбором человека.
                ...(m.crop ? { crop: m.crop } : {}),
                ...(m.from !== undefined ? { from: m.from } : {}),
                ...(m.to !== undefined ? { to: m.to } : {}),
                ...(m.assetId ? { assetId: m.assetId } : {}),
              }
            : m,
        ),
      };
    });
    // setDraft у React — синхронно вне событий не гарантирован: решаем по живому значению.
    if (!kept && !live.current.media.some((m) => m.id === token)) return;
    if (op) conv.start(media.id, media.id, op);
  };
  /** Показать следующий вопрос из очереди (родной лист один на экран). */
  const showAsk = (): void => {
    const next = askQueue.current.shift();
    if (!next) {
      askFile.current = null;
      return;
    }
    askFile.current = next;
    void nativeBridge()?.wizAsk?.({
      title: next.title ?? dict.attachTitle,
      text: next.text,
      actions: next.actions,
      cancel: next.cancel ?? t('cancel'),
      theme: dark ? 'dark' : 'light',
      ...(next.hero ? { hero: next.hero } : {}),
    });
  };

  /**
   * ФАЙЛ ОТБИТ — РОДНЫМ ВОПРОСОМ ПОВЕРХ ЛИСТА (П132 п.19, владелец 25.09.2026: «у пузыря
   * красных ошибок быть не должно»). Ручка (`bad_type`/`too_large`) или оболочка
   * (`toobig` — ужатый всё равно тяжёл, `compress` — сжать нечем) файл не приняли: чинить
   * тут нечего, поэтому действия два — «Выбрать другой файл» (сборке, умеющей открыть окно
   * заново) и «Отмена». Черновая запись (если есть) стоит с кольцом, пока лист открыт, и
   * убирается ответом. Каждый отказ — в журнал.
   */
  const rejectFile = (p: {
    token: string;
    kind: TgMediaKind;
    row: string;
    code: string;
    hero?: MsgFileAsk['hero'];
    /** Отказ ДО заливки (П135 п.11а): по ответу оболочке уходит `drop` — копия стирается,
     *  лист выбора остаётся. У отказа ручки файл уже улетел, сказать нечего. */
    go?: MsgFileAsk['go'];
  }): void => {
    journalLog('msg.fileRejected', { kind: p.kind, code: p.code });
    const text =
      p.code === 'toobig'
        ? ts('attachStillTooBig')
        : p.code === 'compress'
          ? ts('attachCompressFail')
          : p.code === 'too_large'
            ? t('errTooLarge', { mb: mb(RULES.uploadAcceptMax) })
            : t('errBadType');
    // ЗАГОЛОВОК ВОПРОСА (П151 §11р п.3): по беде — «Файл слишком большой» (вес, в том числе
    // после сжатия), «Файл не удалось сжать», «Файл не подходит» (формат).
    const title =
      p.code === 'toobig' || p.code === 'too_large'
        ? t('askFileTooBigTitle')
        : p.code === 'compress'
          ? t('askFileCompressTitle')
          : t('askFileBadTypeTitle');
    if (askDup(p.token, p.row)) return;
    askQueue.current.push({
      token: p.token,
      kind: p.kind,
      row: p.row,
      maxBytes: 0,
      canSqueeze: false,
      go: p.go ?? (() => {}),
      title,
      text,
      actions: nativeMethod('msgAttachOpen') ? [{ key: 'another', label: ts('attachPickAnother') }] : [],
      ...(p.hero ? { hero: p.hero } : {}),
      rejected: true,
    });
    if (!askFile.current) showAsk();
  };

  /**
   * ВОПРОС О СОСТАВЕ — РОДНЫМ НИЗКИМ ЛИСТОМ (ТЗ П163, владелец 26.09.2026: «все варианты,
   * которые приводят к красным ошибкам, замени на предупреждение, которое разрешает
   * конфликт»). Тот же `wizAsk` и та же очередь, что у вопросов про файл: лист один на
   * экран. Выходы — капсулы, «Отмена» — ничего не делать (`null`). Каждый вопрос и
   * ответ — в журнал.
   * ОФОРМЛЕНИЕ (П151 §11р п.3, владелец 27.09.2026): заголовок и полный текст вопроса —
   * из `conflictAsk`, герой — только значок вида (`name` и `meta` пустые: строк имени и
   * меты натив не рисует), у кружка своё слово отмены «Не добавлять кружок».
   */
  const askConflict = (c: MsgConflict, then: (answer: MsgConflictAnswer | null) => void): void => {
    const q = conflictAsk(c, cw);
    journalLog('msg.conflictAsk', { kind: c.kind });
    askQueue.current.push({
      token: '',
      kind: 'photo',
      row: `conflict:${c.kind}`,
      maxBytes: 0,
      canSqueeze: false,
      go: () => {},
      title: q.title,
      text: q.text,
      actions: q.actions,
      hero: { icon: q.icon, name: '', meta: '' },
      ...(q.cancel ? { cancel: q.cancel } : {}),
      onPick: (key) => {
        const answer = q.actions.find((a) => a.key === key)?.key ?? null;
        journalLog('msg.conflictPick', { kind: c.kind, answer: answer ?? 'cancel' });
        then(answer);
      },
    });
    if (!askFile.current) showAsk();
  };

  /** Файл по ответу не идёт: дальше его события молчат, а выбранную копию — стереть. */
  const denyFile = (token: string, picked: boolean): void => {
    deniedFiles.current.add(token);
    if (picked) void nativeBridge()?.msgFileGo?.({ token, action: 'drop' });
  };

  /**
   * ЗАСЛОН ПЕРЕД ФАЙЛОМ (ТЗ П163 п.1, п.3): файл вот-вот встанет в сообщение (`meta` или
   * `picked` новой метки) — если с ним состав ушёл бы двумя сообщениями (альбом при
   * кнопках, кружок при тексте/кнопках), файл и все следующие ждут ответа на вопрос, а
   * плитка не появляется. true — событие забрано здесь. Переэкспорт после «Редактировать»
   * (`replaceKey`) и сборка без родного вопроса идут прежней дорогой.
   */
  const gateFile = (d: MsgFileStateArg): boolean => {
    const token = d.token;
    if (token === '' || d.replaceKey) return false;
    if (deniedFiles.current.has(token)) {
      if (d.status === 'picked') denyFile(token, true);
      return true;
    }
    if (d.status !== 'meta' && d.status !== 'picked') return false;
    if (pickRun.current?.ok.has(token) || metaHeld.current.has(token)) return false;
    if (live.current.media.some((m) => m.id === token)) return false;
    if (!nativeMethod('wizAsk')) return false;
    const held = conflictHeld.current;
    if (held) {
      held.push(d);
      return true;
    }
    const run =
      pickRun.current ??
      (pickRun.current = {
        before: live.current.media,
        replace: livePick.current?.replace === true,
        ok: new Map(),
        allow: false,
        deny: false,
      });
    if (run.deny) {
      denyFile(token, d.status === 'picked');
      return true;
    }
    const kind = tgAttachMediaKind((d.kind as TgAttachKind) ?? 'document', d.mime);
    if (run.allow) {
      run.ok.set(token, kind);
      return false;
    }
    // СОСТАВ ПО ЗАХОДУ, А НЕ ПО НАРИСОВАННОМУ: пропущенные файлы могли ещё не встать.
    const passed = [...run.ok].map(([id, k]): TgDraftMedia => ({ kind: k, id, mime: '', bytes: 0 }));
    const firstReplaces = run.replace && passed.length === 0;
    const now: TgDraft = { ...live.current, media: run.replace ? passed : [...run.before, ...passed] };
    const pins = livePins.current.filter((p) => p.text.trim() !== '').length;
    const c = mediaAddConflict(now, [kind], firstReplaces, pins);
    if (!c) {
      run.ok.set(token, kind);
      return false;
    }
    conflictHeld.current = [d];
    askConflict(c, (answer) => {
      const events = conflictHeld.current ?? [];
      conflictHeld.current = null;
      const tokens = [...new Set(events.map((e) => e.token))];
      const picked = (t: string): boolean => events.some((e) => e.token === t && e.status === 'picked');
      if (answer === null) {
        // «ОТМЕНА» — ИЗ ЭТОГО ЗАХОДА НИЧЕГО (п.1 «×»): и ждущие, и уже вставшие файлы
        // захода уходят, стоявшее до него возвращается.
        run.deny = true;
        for (const t of tokens) denyFile(t, picked(t));
        const gone = new Set(run.ok.keys());
        for (const id of gone) {
          deniedFiles.current.add(id);
          if (inFlight.current.delete(id)) setUploading((n) => Math.max(0, n - 1));
        }
        run.ok.clear();
        if (gone.size > 0) {
          setDraft((dd) => ({
            ...dd,
            media: run.replace ? run.before : dd.media.filter((m) => !gone.has(m.id)),
          }));
        }
        return;
      }
      // Черновик по ответу («Убрать кнопки», «Убрать текст») — сразу и в живое значение:
      // следующие события пачки не ждут перерисовки.
      setDraft((dd) => applyConflict(dd, c, answer));
      live.current = applyConflict(live.current, c, answer);
      const keep = conflictKeep(now, answer, tokens.length, firstReplaces);
      const allowed = new Set(tokens.slice(0, keep));
      for (const t of tokens) if (!allowed.has(t)) denyFile(t, picked(t));
      if (answer === 'keepOne') run.deny = true;
      else run.allow = true;
      for (const e of events) {
        if (allowed.has(e.token)) run.ok.set(e.token, tgAttachMediaKind((e.kind as TgAttachKind) ?? 'document', e.mime));
      }
      // Пропущенные файлы идут прежней дорогой — в том порядке, в каком приехали.
      for (const e of events) if (allowed.has(e.token)) handlers.onFileState(e);
    });
    return true;
  };

  /**
   * ПРАВКА ОТКРЫТОЙ КНОПКИ. Смена вида сохраняет подпись и обнуляет остальное: у ссылки
   * и у метки общего содержимого нет, и тащить чужой адрес в новую роль — врать человеку.
   */
  /** ОДНА ПРАВКА ОДНОЙ КНОПКИ — чистая рука: та же для письма и для черновика страницы. */
  const nextButtonOf = (one: TgButton, field: string, arg: string, value: string | boolean): TgButton => {
      let next: TgButton = one;
      const text = typeof value === 'string' ? value : '';
      if (field === 'text') next = { ...one, text };
      else if (field === 'url' && (one.kind === 'url' || one.kind === 'app')) next = { ...one, url: text };
      else if (field === 'tag' && one.kind === 'tag') next = { ...one, tag: msgBtnTag(one.tag, arg, text) };
      // «И ЗАВЕРШИТЬ ЭТУ ЦЕПОЧКУ» (BACKLOG п.19 (2)): галочка включает и выключает.
      else if (field === 'exit' && one.kind === 'tag') {
        const { exit: _exit, ...rest } = one;
        next = value === true ? { ...rest, exit: 'chain' } : rest;
      }
      // ВСПЛЫВАШКА НА НАЖАТИЕ (И11.8): пусто — нажатие подтверждается молча.
      else if (field === 'reply' && tgButtonTakesReply(one.kind)) {
        next = { ...(one as Extract<TgButton, { kind: 'tag' | 'exit' }>), reply: sanitizeTgReply(text, RULES) };
      } else if (field === 'scope' && one.kind === 'exit') {
        next = { ...one, scope: arg === 'all' ? 'all' : 'chain' };
      }
      // ЦВЕТ (М2.6): «Обычный» — поля `style` у кнопки нет вовсе, а не пустая строка.
      else if (field === 'style') {
        const style = tgButtonStyleOf(arg);
        const { style: _was, ...rest } = one;
        next = style ? ({ ...rest, style } as TgButton) : (rest as TgButton);
      } else if (field === 'kind') {
        // Смена вида ставит подсказанные значения: у отписки — подпись «Отписаться»
        // (если своей нет) и всплывашка по умолчанию, у метки — только всплывашка.
        // ЦВЕТ ПЕРЕЖИВАЕТ СМЕНУ ВИДА (М2.6): он общий у всех видов и к роли кнопки
        // отношения не имеет.
        const reply = msgDefaultReply(arg as TgButton['kind'], btnDict);
        const paint = one.style ? { style: one.style } : {};
        next =
          arg === 'tag'
            ? { text: one.text, kind: 'tag', tag: '', reply, ...paint }
            : arg === 'exit'
              ? {
                  text: one.text.trim() === '' ? t('btnExitText') : one.text,
                  kind: 'exit',
                  scope: 'chain',
                  reply,
                  ...paint,
                }
              : arg === 'app'
                ? { text: one.text, kind: 'app', url: '', ...paint }
                : { text: one.text, kind: 'url', url: '', ...paint };
      }
      return next;
  };

  /**
   * ЗАКРЫТЬ СТРАНИЦУ КНОПКИ. Живые значения гасим ВМЕСТЕ с состоянием: правка, доехавшая
   * после закрытия (запоздавший залп, чужой ключ), не имеет права тронуть письмо.
   */
  const closeBtnPage = (): void => {
    livePend.current = null;
    liveOpen.current = null;
    setPendBtn(null);
    setOpenBtn(null);
  };

  /**
   * ЗАВЕСТИ МЕТКУ ИЗ ЛИСТА КНОПКИ (ТЗ 21.09.2026 §4.5.2): имя появляется в общем списке
   * листа и СРАЗУ встаёт у кнопки — ровно как «Добавить метку» на карточке человека.
   * Пустое имя и служебную пометку не заводим, дубль в список не кладём.
   */
  const addTag = (raw: string): void => {
    const name = raw.trim();
    if (name === '' || isServiceMark(name)) return;
    setNewTags((list) => (list.includes(name) ? list : [...list, name]));
    journalLog('msg.btnTagNew', { len: name.length });
    // В СПРАВОЧНИК КАБИНЕТА СРАЗУ (П130 п.33, владелец 24.09.2026: «должна появляться в
    // списке и обновляться везде»): имя пишется в базу, свод перечитывается — метка видна
    // в этом листе после переоткрытия, на карточке человека и в фильтрах «Чатов».
    void (async () => {
      try {
        const res = await fetch('/api/cabinet/contact-tags', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        journalLog('msg.btnTagSave', { ok: res.ok ? 1 : 0, code: res.status });
        if (res.ok) {
          reloadTags();
          refreshTagCaches();
        }
      } catch {
        journalLog('msg.btnTagSave', { ok: 0, code: 0 });
      }
    })();
    // МЕТКУ У КНОПКИ ЗДЕСЬ НЕ СТАВИМ (журнал 24.09.2026, 13:38: «нажал ОК — ничего не
    // сохранилось»): родной лист шлёт ЗАЛПОМ два хода — `btn.tag.new:<имя>` и следом
    // `btn.tag:<имя>` (MsgBtnTagsPage.addTag → syncPick). Если заводка ставит метку сама,
    // второй ход тем же ключом читается как «снять» (msgBtnTag — переключатель) и стирает
    // только что поставленное. Заводка — только список; ставит метку ход выбора.
  };

  /**
   * СНЯТЬ МЕТКУ У ВСЕХ (смахивание строки листа) — ТОЙ ЖЕ РУЧКОЙ, что карточка человека
   * и «Данные людей»: метка общая для кабинета, своей копии у окна письма нет.
   * Подтверждение показывает родной лист — сюда ключ приходит уже после «да».
   *
   * С кнопки метка снимается СРАЗУ, не дожидаясь ответа ручки: держать у кнопки метку,
   * которой больше нет ни у кого, — обещать человеку несуществующее.
   */
  const dropTagEverywhere = (raw: string): void => {
    const name = raw.trim();
    if (name === '') return;
    setNewTags((list) => list.filter((x) => x !== name));
    // Метка снимается с ЖИВОЙ кнопки: в залпе правок нарисованная уже устарела.
    const now = livePend.current?.button ?? openButton;
    if (now?.kind === 'tag' && now.tag === name) patchButton('btn.tag', '');
    void (async () => {
      try {
        const res = await fetch('/api/cabinet/contact-tags', {
          method: 'DELETE',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name }),
        });
        journalLog('msg.btnTagDel', { ok: res.ok ? 1 : 0, code: res.status });
        // Свод меток кабинета поменялся — перечитываем его тем же ключом кэша, каким
        // список живёт у «Данных людей» (второго источника меток нет).
        if (res.ok) {
          reloadTags();
          refreshTagCaches();
        }
      } catch {
        // Поломка наша — человеку её не показываем (запрет №7), она уходит в журнал.
        journalLog('msg.btnTagDel', { ok: 0, code: 0 });
      }
    })();
  };

  /**
   * ПРАВКА СТРАНИЦЫ КНОПКИ. `value === null` — правка приехала ТАПОМ и значения не несёт:
   * его считает `msgBtnTapValue` уже внутри пересчёта состояния, по живой кнопке (капкан
   * залпа — см. там же).
   */
  const patchButton = (key: string, value: string | boolean | null): void => {
    const open = liveOpen.current;
    if (open === null) return;
    const got = msgBtnKey(key);
    if (!got) return;
    const { field, arg } = got;
    // ОБЩИЙ СПИСОК МЕТОК КАБИНЕТА: заводится и снимается не у кнопки, а у всех.
    if (field === 'tagNew') {
      addTag(arg);
      return;
    }
    if (field === 'tagDel') {
      dropTagEverywhere(arg);
      return;
    }
    // Ключ СТРОКИ страницы (`btn.row.*`, задача А): он открывает лист, а не правит
    // кнопку. Пришёл вебу — просто помечаем в журнале, письмо он не трогает.
    if (field === 'row') {
      journalLog('msg.btnRow', { key: arg });
      return;
    }
    // СБОРКА С ГАЛОЧКОЙ «ГОТОВО»: правки живут в черновике, письмо ждёт нажатия.
    if (livePend.current) {
      setPendBtn((p) => {
        if (!p) return p;
        const v = value === null ? msgBtnTapValue(field, arg, p.button, p.joinPrev) : value;
        if (field === 'joinPrev') return { ...p, joinPrev: msgBtnJoin(arg, v) };
        // СМЕНА ВИДА — С ПАМЯТЬЮ ПРЕЖНИХ НАСТРОЕК (§4.5): она есть только у черновика,
        // старая сборка правит письмо сразу и помнить ей негде.
        const kind = field === 'kind' ? msgBtnKindOf(arg) : null;
        if (kind) {
          const sw = msgBtnKindSwitch(p.button, kind, p.memo, {
            replyTag: btnDict.replyTag,
            replyExit: btnDict.replyExit,
            exitText: t('btnExitText'),
          });
          return { ...p, button: sw.button, memo: sw.memo };
        }
        const next = nextButtonOf(p.button, field, arg, v);
        // Ничего не поменялось — и состояние не трогаем: лишний объект здесь означал бы
        // лишний патч нативу на каждую правку пачки.
        return next === p.button ? p : { ...p, button: next };
      });
      return;
    }
    setDraft((d) => {
      const flat = tgFlatButtons(d.buttons);
      const at = flat[open];
      if (!at) return d;
      const one = at.button;
      const v = value === null ? msgBtnTapValue(field, arg, one, at.joinPrev === true) : value;
      // РАСПОЛОЖЕНИЕ (М2.5) правит не саму кнопку, а её место: ряды пересобираются из
      // флагов, порядок кнопок при этом не меняется.
      if (field === 'joinPrev') {
        const joinPrev = msgBtnJoin(arg, v);
        if (joinPrev === at.joinPrev) return d;
        return {
          ...d,
          buttons: tgRowsFromFlat(
            flat.map((x, i) => ({ button: x.button, joinPrev: i === open ? joinPrev : x.joinPrev })),
            RULES.buttonsPerRowMax,
          ),
        };
      }
      const next = nextButtonOf(one, field, arg, v);
      if (next === one) return d;
      return {
        ...d,
        buttons: tgRowsFromFlat(
          flat.map((x, i) => ({ button: i === open ? next : x.button, joinPrev: x.joinPrev })),
          RULES.buttonsPerRowMax,
        ),
      };
    });
  };

  /**
   * НАМЕРЕНИЯ С РОДНОГО ЭКРАНА. Кнопки под пузырём и капсулы предела возвращаются одним
   * событием `msgTap` — ключи выдал сам веб вместе со слотами.
   */
  const tapped = (key: string, value = '', _from = 0, _to = 0): void => {
    const head = key.split(':')[0] ?? '';
    const id = key.slice(head.length + 1);
    // ПОВТОРИТЬ СОРВАВШУЮСЯ ЗАЛИВКУ (П129.2): оболочка держит копию до ответа ручки.
    // Сборка без метода — копии нет: убираем запись и открываем выбор заново.
    if (head === 'retry' && id !== '') {
      const kind = live.current.media.find((m) => m.id === id)?.kind ?? 'document';
      if (nativeMethod('msgFileRetry')) {
        patchPending(id, { failed: false, progress: 0 });
        void nativeBridge()?.msgFileRetry?.({ token: id });
      } else {
        dropPending(id);
        void nativeBridge()?.msgAttachOpen?.({ kind: attachKindOf(kind) });
      }
      journalLog('msg.fileRetry', { has: nativeMethod('msgFileRetry') ? 1 : 0 });
      return;
    }
    // ПЕРЕСТАНОВКА КНОПОК: натив показал её сразу, а порядок письма правим здесь —
    // второй правды о письме нет.
    if (key === 'reorder') {
      // ПРИКОЛОТЫЕ КНОПКИ НЕ ЕЗДЯТ (З7): старая сборка признака `pinned` не знает и может
      // прислать их в списке — выбрасываем и журналим, порядок письма трогают только его
      // собственные кнопки.
      const all = value.split(' ').filter(Boolean);
      const order = all.filter((one) => !one.startsWith('pin:'));
      if (order.length !== all.length) journalLog('msg.pinnedMove', { keys: all.length - order.length });
      setDraft((d) => {
        // ПОРЯДОК — ПЛОСКИЙ СПИСОК (М2.5): флаг «в одном ряду с предыдущей» едет ВМЕСТЕ с
        // кнопкой, а ряды пересобираются из флагов после перестановки.
        const flat = tgFlatButtons(d.buttons);
        const moved = order
          .map((one) => flatAtKey(flat, one))
          .filter((x): x is (typeof flat)[number] => x !== null);
        // Пришло не всё — порядок не трогаем: терять кнопку на перестановке нельзя.
        if (moved.length !== flat.length) return d;
        return {
          ...d,
          buttons: tgRowsFromFlat(
            moved.map((x) => ({ button: x.button, joinPrev: x.joinPrev })),
            RULES.buttonsPerRowMax,
          ),
        };
      });
      return;
    }
    // «Сжать»: операция раннера есть только у ролика — капсулу у прочих видов веб и не
    // рисует, но ключ проверяем и здесь (сборка в сторе живёт дольше словаря).
    if (head === 'fit' && id !== '') {
      conv.start(id, id, 'fit-size');
      return;
    }
    // ОТРЕЗОК ПОЛЗУНКОМ: натив показал шторку и прислал границы в секундах, а раннера
    // зовём мы — своей руки к нему у натива нет.
    if ((head === 'trim' || head === 'minute') && id !== '' && _to > _from) {
      const source =
        head === 'minute' ? (live.current.media.find((m) => m.id === id)?.derivedFrom ?? id) : id;
      if (head === 'minute') conv.start(id, source, 'round', { from: String(_from), seconds: _to - _from });
      else conv.start(id, source, 'trim', { start: String(_from), end: String(_to) });
      return;
    }
    if (key === 'add-button') {
      // КНОПКА К АЛЬБОМУ (ТЗ П163 п.2): у альбома кнопок не бывает — спрашиваем до
      // страницы кнопки; «Оставить одно фото» — первый кадр остаётся, страница открывается.
      const clash = buttonAddConflict(live.current);
      if (clash && nativeMethod('wizAsk')) {
        askConflict(clash, (answer) => {
          if (answer !== 'keepOne') return;
          const next = applyConflict(live.current, clash, answer);
          live.current = next;
          setDraft(next);
          tapped('add-button', '', 0, 0);
        });
        return;
      }
      // Новая кнопка сразу открывает свою страницу: пустая капсула «Без подписи» в
      // письме — не результат, а полдела. Новая кнопка начинает СВОЙ ряд (М2.5): в один
      // ряд с предыдущей её ставит человек галочкой «Расположение».
      setOpenPin(null);
      if (holdsDraft()) {
        // ЧЕРНОВИК: в письме кнопки нет, пока не нажали «готово»; «назад» — её и не было.
        const fresh = { button: { text: '', kind: 'url' as const, url: '' }, joinPrev: false, fresh: true, memo: {} };
        // Живое значение — СРАЗУ: правки листа могут прийти залпом, не дожидаясь
        // перерисовки, и должны застать страницу уже открытой.
        livePend.current = fresh;
        liveOpen.current = tgFlatButtons(live.current.buttons).length;
        setPendBtn(fresh);
        setOpenBtn(liveOpen.current);
        return;
      }
      setDraft((d) => {
        setOpenBtn(tgFlatButtons(d.buttons).length);
        return { ...d, buttons: [...d.buttons, [{ text: '', kind: 'url', url: '' }]] };
      });
      liveOpen.current = tgFlatButtons(live.current.buttons).length;
      return;
    }
    // Тап по кнопке под пузырём — её страница. Ключ собран нами же: btn:<ряд>:<i>.
    if (head === 'btn') {
      const flat = tgFlatButtons(live.current.buttons);
      const at = flatIndexOfKey(flat, key);
      if (at >= 0) {
        setOpenPin(null);
        const row = flat[at];
        if (holdsDraft() && row) {
          const pend = { button: row.button, joinPrev: row.joinPrev === true, fresh: false, memo: {} };
          livePend.current = pend;
          setPendBtn(pend);
        }
        liveOpen.current = at;
        setOpenBtn(at);
      }
      return;
    }
    // ПРИКОЛОТАЯ КНОПКА (З7): её страница — одна подпись; удалять и переставлять нечего.
    if (head === 'pin') {
      const one = key.slice('pin:'.length);
      if (livePins.current.some((p) => p.key === one)) {
        closeBtnPage();
        setOpenPin(one);
      }
      return;
    }
    // Страница кнопки: ушли назад, удалили, подставили уже поставленную метку.
    if (key === 'btn-back') {
      // «Назад» при черновике — правки выброшены, новая кнопка в письме не появляется.
      closeBtnPage();
      setOpenPin(null);
      return;
    }
    // «ГОТОВО» (галочка справа в шапке, сборка с признаком msgButtonDone): черновик — в письмо.
    if (key === 'btn-done') {
      // Черновик и место берём ЖИВЫЕ: правки листа могли прийти залпом перед самой
      // галочкой, и нарисованный экран о них ещё не знает.
      const held = livePend.current;
      // Адрес кнопки — к годному виду при закрытии страницы (19.09.2026): без схемы
      // дописывается https://, пробелы срезаются; в окне сразу видно итоговый адрес.
      const pend =
        held && (held.button.kind === 'url' || held.button.kind === 'app')
          ? { ...held, button: { ...held.button, url: normalizeButtonUrl(held.button.url) } }
          : held;
      const at = liveOpen.current;
      // КНОПКА БЕЗ ПОДПИСИ НЕ СОХРАНЯЕТСЯ (П130 п.23, владелец 24.09.2026): в Telegram её
      // не бывает. Новая — не кладём вовсе; правка — письмо остаётся прежним.
      if (pend && pend.button.text.trim() === '') {
        journalLog('msg.btnEmpty', { fresh: pend.fresh ? 1 : 0 });
        closeBtnPage();
        setOpenPin(null);
        return;
      }
      if (pend && at !== null) {
        setDraft((d) => {
          const flat = tgFlatButtons(d.buttons).map((x) => ({ button: x.button, joinPrev: x.joinPrev }));
          const rows = pend.fresh
            ? [...flat, { button: pend.button, joinPrev: pend.joinPrev }]
            : flat.map((x, i) => (i === at ? { button: pend.button, joinPrev: pend.joinPrev } : x));
          return { ...d, buttons: tgRowsFromFlat(rows, RULES.buttonsPerRowMax) };
        });
      }
      closeBtnPage();
      setOpenPin(null);
      return;
    }
    if (key === 'btn-remove') {
      // Приколотую кнопку удалить нельзя: без неё развилка сценария осталась бы без
      // своей ветки. Старая сборка «Удалить» ей рисует — отвергаем и журналим (З7).
      if (openPin !== null) {
        journalLog('msg.pinnedRemove', { key: openPin });
        setOpenPin(null);
        return;
      }
      const at = liveOpen.current;
      // Новая кнопка-черновик: удалять нечего, письмо её не видело.
      if (!livePend.current?.fresh) {
        setDraft((d) => ({
          ...d,
          buttons: tgRowsFromFlat(
            tgFlatButtons(d.buttons)
              .filter((_, i) => i !== at)
              .map((x) => ({ button: x.button, joinPrev: x.joinPrev })),
            RULES.buttonsPerRowMax,
          ),
        }));
      }
      closeBtnPage();
      return;
    }
    // СТРОКИ ЛИСТОВ СТРАНИЦЫ КНОПКИ (ТЗ 21.09.2026 раздел 4) приезжают ТАПОМ, а не
    // правкой поля: выбор вида, расположение, цвет, метка в списке, «Добавить метку»,
    // смахивание. Правка от этого не меняется — разбирает её тот же patchButton, и
    // письмо по-прежнему ждёт галочки страницы.
    if (msgBtnKey(key)) {
      // У тапа значения нет вовсе — его считает `msgBtnTapValue` по ЖИВОЙ кнопке уже
      // внутри пересчёта (капкан залпа). Пустую строку не подсовываем: она стёрла бы
      // подпись строкой-полем того же имени.
      patchButton(key, value === '' ? null : value);
      return;
    }
    // Незнакомый ключ — в журнал: разбираем по нему, а не по жалобе (правило
    // «наблюдаемость сначала»).
    journalLog('msg.tap', { key });
  };

  const handlers: NativeMessageOpts = {
    active: true,
    data,
    onSet: (key, value, from, to) => {
      if (key === 'sel') {
        setSel({ from, to });
        return;
      }
      if (key === 'text') {
        const next = typeof value === 'string' ? value : '';
        setSel({ from, to });
        setDraft((d) => {
          const was = tgHtmlToMarks(d.text);
          return { ...d, text: marksToTgHtml(next, shiftMarks(was.marks, was.text, next)) };
        });
        return;
      }
      // «НАЗВАНИЕ» ТРЕКА/ФАЙЛА (П130 п.32в/г): у трека — `title` (Telegram sendAudio),
      // у документа — имя файла (с прежним расширением); пусто — оставляем как было.
      if (key.startsWith('att.name:')) {
        const id = key.slice('att.name:'.length);
        const text = typeof value === 'string' ? value : '';
        setDraft((d) => {
          let changed = false;
          const media = d.media.map((m) => {
            if (m.id !== id) return m;
            const next = msgRenameMedia(m, text);
            if (!next) return m;
            changed = true;
            return next;
          });
          return changed ? { ...d, media } : d;
        });
        journalLog('msg.attachRename', { kind: live.current.media.find((m) => m.id === id)?.kind ?? '' });
        return;
      }
      // СТРАНИЦА КНОПКИ: подпись, адрес, метка, вид и «кого снимаем» — ключи `btn.*`
      // (и ключи листа меток, если сборка прислала его собственные — см. msgBtnKey).
      if (msgBtnKey(key)) {
        // У ПРИКОЛОТОЙ кнопки правится только подпись, и уходит она в ответ анкеты (З7).
        if (openPin !== null) {
          if (key === 'btn.text' && typeof value === 'string') {
            const one = openPin;
            setPins((list) => list.map((p) => (p.key === one ? { ...p, text: value } : p)));
            onPinned?.(one, value);
          }
          return;
        }
        patchButton(key, value);
      }
    },
    onTap: (key, value, from, to) => tapped(key, value, from, to),
    // Слоты пузыря натив открывает сам (лист «Вложение», правка текста в пузыре) — вебу
    // остаётся знать, что происходит, и держать свежими его строки.
    //
    // ТАП ПО ЛЮБОМУ МЕДИА → ЛИСТ «ВЛОЖЕНИЕ» (П132 п.22) ВЕБ НЕ ДУБЛИРУЕТ ВЫЗОВОМ
    // `msgAttachOpen`: этот метод открывает СИСТЕМНОЕ ОКНО ВЫБОРА ФАЙЛА под вид (см. его
    // описание в native-app.ts), и по тапу на пузыре человек получил бы галерею поверх
    // листа. Лист по `msgSlotTap` поднимает сам натив (MessageScreen `screen.onSlot` →
    // `msgAttach()`), для всех видов одинаково; веб отвечает за то, чтобы строки листа
    // были у любого вида (включая гифку и кружок), — в журнал пишем вид, по которому тапнули.
    onSlot: (slot) =>
      journalLog('msg.slot', { slot, shape: slot === 'media' ? draftShape(live.current) : '' }),
    onFormat: (kind, from, to, url, language) => {
      // Механика пункта — Telegram-iOS (ТЗ П150 §2.5–2.6): блок может вставить переводы
      // строк, тогда текст меняется и выделение схлопывается в каретку за блоком. Считаем
      // по живому черновику — тем же, что уедет в setDraft.
      const was = tgHtmlToMarks(live.current.text);
      const got = applyFormat(was.text, was.marks, from, to, kind as TgFormatKind, url || undefined, language || undefined);
      setSel(got.collapse ? { from: got.caret, to: got.caret } : { from, to });
      setDraft((d) => {
        const now = tgHtmlToMarks(d.text);
        const res = applyFormat(now.text, now.marks, from, to, kind as TgFormatKind, url || undefined, language || undefined);
        return { ...d, text: marksToTgHtml(res.text, res.marks) };
      });
    },
    onAttachPick: (kind, replace) => {
      // Новый заход выбора (ТЗ П163): состав заслона считается заново.
      pickRun.current = null;
      // Тап по строке «Сейчас в сообщении» — это «Изменить» (владелец 21.09.2026):
      // оболочка сказала `replace`, и приехавший файл ЗАМЕНИТ стоящее, что бы ни
      // говорили группы листа.
      if (replace === true) {
        setPick({ kind: kind as TgAttachKind, replace: true });
        return;
      }
      // Строка «Заменить» — та, которой в группе «Добавить» нет: об этом веб уже сказал
      // листу, а здесь просто запоминаем, чем окажется приехавший файл.
      const opts = msgAttachSheet(live.current, dict, livePins.current, {
        origin,
        locale,
        ...(nativeMethod('msgFileGo') ? { upload: { url: `${origin}/api/cabinet/media`, field: 'file' } } : {}),
      });
      // Вкладка стоящего вида (П182): кружок/гифка/голосовое того же вида — замена.
      setPick({ kind: kind as TgAttachKind, replace: attachPickReplaces(live.current, opts, kind, false) });
    },
    /** ПОРЯДОК ПАЛЬЦЕМ (П129.3): лист прислал ключи в новом порядке. Пришло не всё или
     *  чужое — порядок не трогаем: терять кадр на перестановке нельзя. */
    onAttachOrder: (keys) => {
      const order = keys.split(' ').filter(Boolean);
      setDraft((d) => {
        if (order.length !== d.media.length) return d;
        const byId = new Map(d.media.map((m) => [m.id, m] as const));
        const next = order.map((k) => byId.get(k)).filter((m): m is TgDraftMedia => m !== undefined);
        if (next.length !== d.media.length) return d;
        return { ...d, media: next };
      });
      journalLog('msg.mediaOrder', { n: order.length });
    },
    onAttachDrop: (key) => {
      // «Превью ссылки» убирается флагом (сама ссылка в тексте остаётся), файл — из
      // списка. Пустой ключ (старая сборка с кнопкой «Убрать вложение») снимает всё.
      if (key === 'preview') {
        setDraft((d) => ({ ...d, previewOff: true }));
        return;
      }
      setDraft((d) => ({ ...d, media: key === '' ? [] : d.media.filter((m) => m.id !== key) }));
      // СЧЁТЧИК ЗАЛИВКИ СНИМАЕТСЯ ВМЕСТЕ С ФАЙЛОМ (П135 п.6, просьба натива): отменённый
      // экспорт/заливка (крестик, новый заход редактора) не должны держать окно под замком.
      const gone = key === '' ? [...inFlight.current] : inFlight.current.has(key) ? [key] : [];
      for (const one of gone) {
        inFlight.current.delete(one);
        setUploading((n) => Math.max(0, n - 1));
      }
    },
    onOption: (key, on) => {
      if (key === 'spoiler' || key === 'captionAbove' || key === 'previewOff') {
        setDraft((d) => ({ ...d, [key]: on }));
      }
    },
    /**
     * ФАЙЛ ГРУЗИТ САМА ОБОЛОЧКА (ТЗ 20.09.2026 V.4 п.5, владелец: «тяжёлый исходник в
     * приложении на сервер не идёт»). Оболочка шлёт одни метаданные, РЕШАЕТ ВЕБ:
     * подходит — «вези как есть»; не подходит — родной лист с вопросом, и только по
     * слову человека ролик жмётся на устройстве или формат переводит раннер.
     */
    onFileState: (d) => {
      // ВОПРОС О СОСТАВЕ ДО ПЛИТКИ (ТЗ П163 п.1, п.3): альбом при кнопках, кружок при тексте.
      if (gateFile(d)) return;
      const want = (d.kind as TgAttachKind) ?? 'document';
      const kind = tgAttachMediaKind(want, d.mime);
      const token = d.token;
      const go = (action: 'upload' | 'compress' | 'drop', maxBytes?: number): void => {
        void nativeBridge()?.msgFileGo?.({
          token,
          action,
          // Вид в адресе — настоящий: ручка сверяет по нему тип файла по байтам. Следом —
          // ОПИСАНИЕ ОТ ТЕЛЕФОНА (П136 п.4б, доктрина «максимум на телефоне»): с ним ручка
          // файл на раннер не везёт (длину и стороны сверяет по контейнеру на месте).
          url: `${origin}/api/cabinet/media?kind=${encodeURIComponent(kind)}${phoneQuery(d)}`,
          ...(maxBytes ? { maxBytes } : {}),
        });
      };
      // ФАЙЛ ЕЩЁ КОПИРУЕТСЯ ИЗ ГАЛЕРЕИ — ПЛИТКА УЖЕ СТОИТ (П130 п.14, владелец 24.09.2026:
      // «реакция мгновенная»): у записи только место в отметке и кадр из галереи; вес,
      // длительность и локальный адрес доедут ходом `picked` той же меткой.
      if (d.status === 'meta') {
        if (token === '') return;
        // Копия обогнала кадр — плитка уже стоит: дописываем только кадр.
        if (live.current.media.some((m) => m.id === token)) {
          if (d.poster) patchPending(token, { poster: d.poster });
          return;
        }
        // ФОРМАТ МИМО КАНАЛА ВИДЕН УЖЕ ПО ТИПУ (П132 п.19): плитку не ставим вовсе — вопрос
        // придёт ходом `picked`, и строка «появилась — исчезла» не мелькнёт. Место в отметке
        // и кадр помним: встанет файл после «Обработать» туда же.
        if (attachNeedsAsk(kind, d.mime)) {
          metaHeld.current.set(token, {
            ...(d.poster ? { poster: d.poster } : {}),
            ...(d.order !== undefined ? { order: d.order } : {}),
            ...(d.assetId ? { assetId: d.assetId } : {}),
          });
          return;
        }
        placePending(
          {
            kind,
            id: token,
            mime: d.mime,
            bytes: 0,
            ...(d.name ? { name: d.name } : {}),
            pending: true,
            local: '',
            ...(d.poster ? { poster: d.poster } : {}),
            ...(d.order !== undefined ? { order: d.order } : {}),
            // Личность снимка в галерее (П173) — известна уже по `meta`.
            ...(d.assetId ? { assetId: d.assetId } : {}),
            progress: 0,
          },
          d.replaceKey,
        );
        return;
      }
      if (d.status === 'picked') {
        const max = RULES.kinds[kind].maxBytes;
        // КРУЖОК ВЕСОМ НЕ РУГАЕМ (П129.4): его пересобирает телефон (квадрат 640, отрезок до
        // минуты) — вес исходника ничего не значит.
        const heavy = kind !== 'round' && d.bytes > 0 && d.bytes > max;
        // СТОРОНЫ ФОТО (Bot API sendPhoto: сумма сторон ≤ 10000, соотношение ≤ 20) —
        // тоже ВОПРОС, а не отказ ручки: снимок оболочка больше не уменьшает молча, и
        // слишком широкий кадр доехал бы до заливки и вернулся сухим «bad_type».
        // Числа — из свода правил каналов, через общий хелпер (второй правды нет).
        const badDims =
          kind === 'photo' &&
          !photoDimsOk(d.width && d.height ? { width: d.width, height: d.height } : null);
        // ФОРМАТ МИМО КАНАЛА — отдельная беда, и она может прийти ВМЕСТЕ с весом.
        const badFormat = attachNeedsAsk(kind, d.mime);
        // Плитка из галереи уже стоит по `meta` (П130 п.14): её место в отметке и кадр
        // переходят в запись — второй плитки не кладём.
        const had = token !== '' ? live.current.media.find((m) => m.id === token) : undefined;
        const held = metaHeld.current.get(token);
        metaHeld.current.delete(token);
        const poster = d.poster ?? had?.poster ?? held?.poster;
        const order = d.order ?? had?.order ?? held?.order;
        const assetId = d.assetId ?? had?.assetId ?? held?.assetId;
        const hero = attachAskHero(
          want,
          { kind, ...(d.name ? { name: d.name } : {}), bytes: d.bytes, ...(d.seconds !== undefined ? { seconds: d.seconds } : {}), ...(poster ? { poster } : {}), ...(d.local ? { local: d.local } : {}) },
          locale,
          dict,
        );
        // ФОРМАТ, КОТОРЫЙ КАНАЛ НЕ ПРИМЕТ И ПЕРЕВЕСТИ НЕЧЕМ (П135 п.11а, владелец 25.09.2026:
        // шрифт из «Файлов» — «сначала добавился файл, потом плашка»): отказ ЗДЕСЬ, тем же
        // правилом, что у ручки (`tgMediaMimeAccepted`), до записи и до заливки; строка не
        // появляется, лист выбора остаётся (ответ уходит оболочке `drop`).
        if (!badFormat && d.mime !== '' && !tgMediaMimeAccepted(kind, d.mime)) {
          if (had) dropPending(token);
          rejectFile({ token, kind, row: want, code: 'bad_type', hero, go });
          return;
        }
        const record: TgDraftMedia = {
          kind,
          id: token,
          mime: d.mime,
          bytes: d.bytes,
          ...(d.width !== undefined ? { width: d.width } : {}),
          ...(d.height !== undefined ? { height: d.height } : {}),
          ...(d.seconds !== undefined ? { seconds: d.seconds } : {}),
          ...(d.name ? { name: d.name } : {}),
          pending: true,
          local: d.local ?? '',
          // Кадр круга, снятый на телефоне (П129.4), — пузырь рисует его сразу.
          ...(poster ? { poster } : {}),
          ...(order !== undefined ? { order } : {}),
          // КАК ВЫБРАЛ ЧЕЛОВЕК В РЕДАКТОРЕ (П171) и личность снимка (П173) — в черновик.
          ...(d.crop ? { crop: d.crop } : {}),
          ...(d.from !== undefined ? { from: d.from } : {}),
          ...(d.to !== undefined ? { to: d.to } : {}),
          ...(assetId ? { assetId } : {}),
          progress: 0,
        };
        // ПОДХОДИТ — НИЧЕГО НЕ СПРАШИВАЕМ И НЕ ТРОГАЕМ (владелец 20.09.2026). ВЛОЖЕНИЕ
        // ВСТАЁТ СРАЗУ (П129.2, «как в Telegram»): запись с меткой оболочки и локальным
        // адресом — пузырь и лист рисуют её в ту же секунду, заливка идёт следом под кольцом.
        if (!heavy && !badDims && !badFormat) {
          if (had) patchPending(token, record);
          else if (token !== '') placePending(record, d.replaceKey);
          setFailed('');
          go('upload');
          return;
        }
        // НЕ ПОДХОДИТ — ПРОВЕРКА ДО ЗАПИСИ (П132 п.19, владелец 25.09.2026: «строка появилась
        // и исчезла»): в сообщение файл НЕ встаёт вовсе, пока человек не ответит на вопрос;
        // плитку, которую успела поставить галерея (`meta`), снимаем сразу. Встанет запись
        // только по «Сжать»/«Обработать» (`place` в вопросе).
        if (had) dropPending(token);
        journalLog('msg.fileRejected', {
          kind,
          code: [badFormat ? 'format' : '', heavy ? 'heavy' : '', badDims ? 'dims' : ''].filter(Boolean).join('+'),
        });
        // Ужать на устройстве можно ролик И СНИМОК (правило владельца «фото без нужды не
        // трогать»: оболочка больше не перерисовывает его при выборе — лестница
        // 4096 → 2560 → 1920 → 1280 идёт только по слову человека и заодно чинит стороны).
        // (кружок сюда не попадает: его вес не считается, П129.4).
        const canSqueeze = (heavy || badDims) && (kind === 'photo' || kind === 'video' || kind === 'animation');
        // «ЗАГРУЗИТЬ ДРУГОЙ ФАЙЛ» — только сборке, умеющей открыть окно выбора заново
        // (хвост ТЗ блока V): мёртвая капсула хуже её отсутствия, а старой сборке из
        // родного вопроса остаётся прежняя «Отмена» (запрет №8).
        const canAnother = nativeMethod('msgAttachOpen');
        // ФОРМАТ И ВЕС НАЗЫВАЮТСЯ ВМЕСТЕ (владелец 20.09.2026: «говоришь, что не
        // подходит формат, но ничего не говоришь про размер»). Формат не тот И файл
        // тяжёлый — одна фраза про обе беды; переделка обещается только тогда, когда
        // её есть кому сделать (устройство жмёт снимок и ролик, раннер переводит
        // формат), иначе просим другой файл.
        const text = badFormat
          ? ts(
              heavy
                ? canSqueeze
                  ? 'attachBadFormatBigAsk'
                  : 'attachBadFormatBigOnly'
                : 'attachBadFormatAsk',
              {
                ext: mimeWord(d.mime),
                list: mimeWords(RULES.kinds[kind].accept),
                has: Math.ceil(d.bytes / (1024 * 1024)),
                max: mb(max),
              },
            )
          : heavy
            ? ts(canSqueeze ? 'attachTooBigAsk' : 'attachTooBigOnly', {
                kind: dict.fileName(kind),
                has: Math.ceil(d.bytes / (1024 * 1024)),
                max: mb(max),
              })
            : ts('attachBadDims');
        const actions = [
          ...(canSqueeze
            ? [{ key: 'compress', label: ts('attachCompress') }]
            : heavy || badDims
              ? []
              : [{ key: 'convert', label: ts('attachProcess') }]),
          ...(canAnother ? [{ key: 'another', label: ts('attachPickAnother') }] : []),
        ];
        // ВОПРОСЫ — ПО ОЧЕРЕДИ (П129.2): файлы приезжают пачкой, а родной лист один; пока
        // человек отвечает про первый, остальные ждут своей очереди, а не теряются.
        if (askDup(token, want)) return;
        askQueue.current.push({
          token,
          kind,
          row: want,
          maxBytes: max,
          canSqueeze,
          go,
          // ЗАГОЛОВОК ВОПРОСА (П151 §11р п.3): вес — «Файл слишком большой», формат и
          // стороны кадра — «Файл не подходит».
          title: t(heavy && !badFormat ? 'askFileTooBigTitle' : 'askFileBadTypeTitle'),
          text,
          actions,
          hero,
          ...(token !== '' ? { place: record } : {}),
          ...(d.replaceKey ? { replaceKey: d.replaceKey } : {}),
        });
        if (!askFile.current) showAsk();
        return;
      }
      // Оболочка занята файлом — письмо «Готово» не пускает (как при заливке вебом);
      // кольцо на кадре ведёт долю (П129.2). Счётчик — по файлу, не по каждому событию.
      if (d.status === 'compressing' || d.status === 'uploading') {
        setFailed('');
        if (!inFlight.current.has(token)) {
          inFlight.current.add(token);
          setUploading((n) => n + 1);
        }
        // ДОЛЯ И У СЖАТИЯ (П135 п.6, просьба натива): оболочка шлёт `progress` и в
        // `compressing` — кольцо ведёт одну шкалу на весь путь файла, не откатываясь.
        patchPending(token, { progress: d.progress ?? 0, failed: false });
        return;
      }
      if (inFlight.current.delete(token)) setUploading((n) => Math.max(0, n - 1));
      const body = (d.body ?? {}) as {
        ok?: boolean;
        id?: string;
        mime?: string;
        bytes?: number;
        width?: number;
        height?: number;
        seconds?: number;
        name?: string;
        poster?: string;
        hasAudio?: boolean;
        title?: string;
        performer?: string;
        error?: string;
        timing?: Record<string, unknown>;
      };
      if (d.status !== 'done' || body.ok !== true || !body.id) {
        journalLog('msg.uploadFail', { kind, code: d.code ?? body.error ?? 'upload' });
        // КОПИИ НА ТЕЛЕФОНЕ БОЛЬШЕ НЕТ (перезапуск) — повторять нечего: запись убираем и
        // открываем выбор заново под ту же строку листа.
        if (d.code === 'gone') {
          dropPending(token);
          void nativeBridge()?.msgAttachOpen?.({ kind: want });
          return;
        }
        // ФАЙЛ ОТБИТ (вес, формат; ужатый всё равно тяжёл; сжать нечем) — повторять
        // бессмысленно: родной вопрос поверх листа, запись убирается его ответом
        // (П132 п.19). Красной строки у пузыря для отказов файла больше нет.
        const refused =
          d.code === 'toobig' || d.code === 'compress'
            ? d.code
            : body.error === 'too_large' || body.error === 'bad_type'
              ? body.error
              : '';
        if (refused !== '' && nativeMethod('wizAsk')) {
          rejectFile({ token, kind, row: want, code: refused });
          return;
        }
        // СЕТЬ — «Не загрузилось · Повторить» НА САМОМ КАДРЕ (П129.2): ряд с действием, а не
        // красный текст у пузыря. Записи нет (старая оболочка без метки) — сказать больше
        // негде, остаётся строка окна.
        if (refused === '' && token !== '' && live.current.media.some((m) => m.id === token)) {
          patchPending(token, { failed: true });
          return;
        }
        setFailed(
          d.code === 'toobig'
            ? ts('attachStillTooBig')
            : d.code === 'compress'
              ? ts('attachCompressFail')
              : body.error === 'too_large'
                ? t('errTooLarge', { mb: mb(RULES.uploadAcceptMax) })
                : body.error === 'bad_type'
                  ? t('errBadType')
                  : t('errUpload'),
        );
        dropPending(token);
        return;
      }
      // ТАЙМИНГИ СЕРВЕРА (П136 п.4, доктрина §11.4 п.4): приём, проба, переделка, запись — мс.
      journalLog('msg.upload', {
        kind,
        bytes: body.bytes ?? d.bytes,
        ...(body.timing && typeof body.timing === 'object' ? { timing: body.timing } : {}),
      });
      const mime = body.mime ?? d.mime;
      const real = tgAttachMediaKind(want, mime);
      const media: TgDraftMedia = {
        kind: real,
        id: body.id,
        mime,
        bytes: body.bytes ?? d.bytes,
        ...(body.width !== undefined ? { width: body.width } : {}),
        ...(body.height !== undefined ? { height: body.height } : {}),
        ...(body.seconds !== undefined ? { seconds: body.seconds } : {}),
        ...(body.name ? { name: body.name } : {}),
        ...(body.poster ? { poster: body.poster } : {}),
        ...(body.hasAudio !== undefined ? { hasAudio: body.hasAudio } : {}),
        ...(body.title ? { title: body.title } : {}),
        ...(body.performer ? { performer: body.performer } : {}),
      };
      // СТАРАЯ ОБОЛОЧКА (без метки в событии) — по-прежнему кладём файл по факту заливки.
      if (token === '' || !live.current.media.some((m) => m.id === token)) {
        if (token === '') putMedia(media, autoOp(real, mime, media));
        return;
      }
      settlePending(token, media, autoOp(real, mime, media));
    },
    /** Ответ на родной вопрос про файл: «Сжать»/«Обработать» — делаем, иначе забываем
     *  (и черновую запись с кадра тоже: в сеть файл не уходил). */
    onAskPick: (key) => {
      const ask = askFile.current;
      askFile.current = null;
      // Вопрос о составе (ТЗ П163): ответ забирает тот, кто спрашивал.
      if (ask?.onPick) {
        ask.onPick(key);
        showAsk();
        return;
      }
      if (ask) {
        if (ask.rejected) {
          // ОТКАЗ (П132 п.19): чинить нечего — запись с кольцом убираем ПОСЛЕ ответа, а
          // «Выбрать другой файл» открывает то же окно выбора, что строка листа.
          // Отказ до заливки (П135 п.11а) — оболочке `drop`: копия стирается, лист выбора стоит.
          ask.go('drop');
          dropPending(ask.token);
          if (key === 'another') void nativeBridge()?.msgAttachOpen?.({ kind: ask.row });
        } else if (key === 'compress' && ask.canSqueeze) {
          // Согласился — только теперь файл встаёт в сообщение (проверка шла ДО записи).
          if (ask.place) placePending(ask.place, ask.replaceKey);
          ask.go('compress', ask.maxBytes);
        } else if (key === 'convert') {
          if (ask.place) placePending(ask.place, ask.replaceKey);
          ask.go('upload');
        } else if (key === 'another') {
          // «Загрузить другой файл»: выбранное забываем (в сеть оно не уходило) и открываем
          // то же системное окно, что по тапу строки листа «Вложение».
          ask.go('drop');
          dropPending(ask.token);
          void nativeBridge()?.msgAttachOpen?.({ kind: ask.row });
        } else {
          // «Отмена» — файл в письмо не ложится вовсе: в сеть он и не уходил.
          ask.go('drop');
          dropPending(ask.token);
        }
      }
      showAsk();
    },
    // ФАЙЛЫ КЛАДУТСЯ ПО ОЧЕРЕДИ. Альбом приезжает из родного выбора пачкой событий, а
    // порядок кадров — это порядок, который человек видел в пикере: заливать их разом
    // значило бы складывать альбом в том порядке, в каком отвечает сеть.
    onFile: (kind, name, mime, dataUrl, replaceKey) => {
      fileQueue.current = fileQueue.current
        .then(() => takeFile(kind, name, mime, dataUrl, replaceKey))
        // Сорвалась сеть — очередь не должна умирать вместе с одним файлом: говорим
        // человеку одной фразой, подробность уходит в журнал (наблюдаемость сначала).
        .catch(() => {
          journalLog('msg.uploadFail', { kind, code: 'network' });
          setFailed(t('errUpload'));
        });
    },
    onSubmit: () => {
      // Страница кнопки могла остаться открытой — письмо забирают целиком.
      closeBtnPage();
      setOpenPin(null);
      leftBy.current = 'submit';
      // Пустое, недоделанное или собранное из двух сообщений письмо «Готово» не
      // выпускает (И9.8): в это время кружок в шапке натива выключен (`canDone`).
      // MARKDOWN — ТОЛЬКО ПРИ ОТПРАВКЕ (ТЗ П150 §5, как у Telegram): **жирный**, `код`, ```блок```
      // превращаются здесь; выход через «‹» оставляет черновик с маркерами как есть.
      if (msgCanDone(live.current, busyNow, livePins.current)) {
        const text = markdownHtml(live.current.text);
        onSave(text === live.current.text ? live.current : { ...live.current, text });
        return;
      }
      // НЕ ОДНО СООБЩЕНИЕ, НО ЧИНИТСЯ ОТВЕТОМ (ТЗ П163 п.4): текст длиннее подписи при
      // медиа, старое «кружок + текст» / «альбом + кнопки» — вопрос, а не молчание.
      const pinsN = livePins.current.filter((p) => p.text.trim() !== '').length;
      const clash = busyNow ? null : doneConflict(live.current, pinsN);
      if (clash && nativeMethod('wizAsk')) {
        askConflict(clash, (answer) => {
          if (answer === null) return;
          const next = applyConflict(live.current, clash, answer);
          live.current = next;
          setDraft(next);
        });
      }
    },
    onCancel: () => {
      // «‹» и свайп ничего не теряют (М2.2): набранное уже уехало в ответы анкеты.
      leftBy.current = 'cancel';
      onCancel();
    },
  };
  useNativeMessage(handlers);

  return null;
}
